// src/views/EOrders.jsx
import React, { useEffect, useMemo, useState, useRef } from 'react';
import Split from 'react-split';
import './EOrders.css';
import { showToast, hideToast, updateToast } from '../utils/toast';

import {
  FaPlus, FaRegSave, FaTrash, FaEdit, FaSearch, FaTimes,
  FaListUl, FaEye, FaFileExport, FaUndo, FaStore, FaCalculator,
  FaPrint, FaListAlt, FaBoxOpen, FaSync, FaChevronDown, FaDollarSign, FaUpload, FaDownload,
  FaHistory, FaPaperclip, FaUserPlus, FaUserMinus, FaEllipsisH, FaClone, FaCopy
} from 'react-icons/fa';
import { SiShopify, SiAmazon, SiEbay, SiWalmart, SiWoocommerce } from 'react-icons/si';

import EOrderItems from './EOrderItems';
import ShippingCalculator from './ShippingCalculator.jsx';
import NewEOrder from './NewEOrder.jsx';
import EOrderEditWorkspace from './EOrderEditWorkspace.jsx';
import EOrderLabelModal from './EOrderLabelModal.jsx';
import OrderItemsPicker from '../components/OrderItemsPicker';
import SyncResultsModal from '../components/SyncResultsModal';
import SyncSpinnerModal from '../components/SyncSpinnerModal';

const API = import.meta.env.VITE_API_URL;
const DEFAULT_LIMIT = 50;
const MIN_COL_W = 40;
const SEARCH_DEBOUNCE_MS = 300; // debounce for unified fetch
const LS_REFRESH_KEY = 'eorders.refreshIntervalSec';

// LocalStorage keys
const LS_LIMIT_KEY = 'eordersLimit';
const LS_SORT_KEY  = 'eordersSort.v1';
const LS_DATE_PRESET = 'eordersDatePreset.v1';
// Auto-track statuses now always enabled (no toggle persisted)

// ---- eOrders filter persistence ----
const EO_K_Q            = 'eorders.q';
const EO_K_CLIENTNAME   = 'eorders.clientname';
const EO_K_CLIENTID     = 'eorders.clientid';
const EO_K_FROM         = 'eorders.from';
const EO_K_TO           = 'eorders.to';
const EO_K_DATEFIELD    = 'eorders.datefield';
const EO_K_SHOWDELETED  = 'eorders.showDeleted';
const EO_K_PRESET       = 'eorders.datePreset';
const EO_K_SORT         = 'eorders.sort';
const EO_K_LIMIT        = 'eorders.limit';
const EO_K_SERVICETYPE  = 'eorders.filter.servicetype';
const EO_K_STORESTATUS  = 'eorders.filter.storestatus';
// NEW multi-select for our_status
const EO_K_OURSTATUS_IDS = 'eorders.filter.ourstatusIds';
const EO_K_BILLED       = 'eorders.filter.billed';

// ---- Our Status ladder (ordering helpers) ----
const STATUS_ORDER = [
  'NEW',
  'REQUESTED',
  'PICKING',
  'PICKED',
  'PACKING',
  'READY TO SHIP',
  'SHIPPED',
  'DELIVERED',
  'BACK ORDER',
  'CANCELLED',     // <-- added here (id 97)
  'SKIPPED',
  'ERROR'
];
const statusIndex = (name) => {
  const i = STATUS_ORDER.indexOf(String(name || '').toUpperCase());
  return i === -1 ? 999 : i;
};

// Helper: get status id by name from a list
const getStatusIdByName = (name, list) => {
  const n = String(name || '').toUpperCase();
  const hit = (Array.isArray(list) ? list : []).find(o => String(o.name || '').toUpperCase() === n);
  return hit?.id ?? null;
};

const getLS = (k, d='') => {
  try { const v = localStorage.getItem(k); return v ?? d; } catch { return d; }
};
const getLSJSON = (k, d) => {
  try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : d; } catch { return d; }
};

function getLoggedInUser() {
  try {
    return JSON.parse(localStorage.getItem('currentUser') || localStorage.getItem('user') || '{}') || {};
  } catch { return {}; }
}
function getLoggedInClientId() {
  const u = getLoggedInUser();
  const cid = u.clientid ?? u.clientId ?? u.clientID ?? u.client ?? null;
  const n = Number(cid);
  return Number.isFinite(n) && n > 0 ? n : null;
}
function getLoggedInClientName() {
  const u = getLoggedInUser();
  return u.clientname || u.clientName || '';
}

function getRefreshSeconds() {
  try {
    // Primary: explicit seconds persisted in LS
    const raw = localStorage.getItem(LS_REFRESH_KEY) || '';
    const v = Number(raw);
    if (Number.isFinite(v)) {
      if (v <= 0) return 0;
      if (v >= 60) return v; // already seconds
      // Backward-compat: old value may have been saved as minutes (e.g., "5").
      // Treat values < 60 as minutes and migrate to seconds.
      const sec = v * 60;
      try { localStorage.setItem(LS_REFRESH_KEY, String(sec)); } catch {}
      return sec;
    }
    // Fallback: user's refreshtimeout in MINUTES → convert to seconds
    const u = JSON.parse(localStorage.getItem('user') || '{}') || {};
    const minutes = Number(u.refreshtimeout || 0);
    if (Number.isFinite(minutes) && minutes >= 0) return minutes * 60;
    return 0;
  } catch { return 0; }
}
function isClientUser() {
  const u = getLoggedInUser();
  const roleStr = [u.role, u.userrole, u.type, u.usertype, u.userType]
    .filter(Boolean)
    .map(v => String(v).toLowerCase())
    .join(' ');
  const explicitClient = (u.isClient === true) || (u.isclient === true) || (u.client === true) || (u.clientuser === true);
  const explicitInternal = (u.isInternal === true) || (u.internal === true);
  const hasClientId = !!getLoggedInClientId();
  if (explicitInternal) return false;
  if (explicitClient) return true;
  if (hasClientId) return true;
  return roleStr.includes('client') && !roleStr.includes('internal');
}

function isAdminUser() {
  const u = getLoggedInUser();
  const flag = u?.isadmin ?? u?.isAdmin ?? u?.admin;
  const norm = typeof flag === 'string' ? flag.trim().toLowerCase() : flag;
  return norm === true || norm === 'true' || norm === '1' || Number(norm) === 1;
}

// --- reference lists used by editor (Ship-To) ---
const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
];
const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'MX', name: 'Mexico' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
];

// Base shape used when loading into editor
const emptyEOrder = {
  id: null,
  datecreated: '',
  clientid: '',
  clientname: '',
  contactemail: '',
  ordernumber: '',
  orderdate: '',
  our_order_status: 0,
  total: '',
  notes: '',

  // Ship-To (editable)
  shippingcompany: '',
  shippingfirstname: '',
  shippinglastname: '',
  shippingaddress1: '',
  shippingaddress2: '',
  shippingcity: '',
  shippingstate: '',
  shippingcountry: 'US',
  shippingpostalcode: '',
  shippingphone: '',
  shippingemail: '',
  shippingmethod: '',
  shippingaccount: '',
  deleted: 0,
};

function getInitialLimit() {
  const persisted = Number(getLS(EO_K_LIMIT, '')) || Number(getLS(LS_LIMIT_KEY, ''));
  return persisted || DEFAULT_LIMIT;
}
function getInitialSort() {
  const persisted = getLSJSON(EO_K_SORT, null);
  if (persisted) return { sortField: persisted.sortField || 'orderdate', sortOrder: persisted.sortOrder || 'DESC' };
  try {
    const raw = localStorage.getItem(LS_SORT_KEY);
    if (!raw) return { sortField: 'orderdate', sortOrder: 'DESC' };
    const j = JSON.parse(raw);
    return { sortField: j?.sortField || 'orderdate', sortOrder: j?.sortOrder === 'ASC' ? 'ASC' : 'DESC' };
  } catch {
    return { sortField: 'orderdate', sortOrder: 'DESC' };
  }
}

const pad2 = (n) => String(n).padStart(2, '0');
const toSQLDateTime = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d)) return null;
  return `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};
const toSQLDateStart = (yyyy_mm_dd) => {
  if (!yyyy_mm_dd) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(yyyy_mm_dd)) return `${yyyy_mm_dd} 00:00:00`;
  const dt = toSQLDateTime(yyyy_mm_dd);
  return dt ? dt.split(' ')[0] + ' 00:00:00' : null;
};

// Date preset helpers
const fmtYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
};
const startOfWeekMon = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = x.getDay(); // 0=Sun..6=Sat
  const diff = (day === 0 ? -6 : 1 - day); // Monday first
  x.setDate(x.getDate() + diff);
  return x;
};
const endOfWeekMon = (d) => {
  const s = startOfWeekMon(d);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  return e;
};
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth   = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfYear  = (y) => new Date(y, 0, 1);
const endOfYear    = (y) => new Date(y, 11, 31);

// ---- eOrders Split persistence ----
const EORD_SPLIT_KEY = 'eordersSplitHeights';
const EORD_SPLIT_PCT = 'eordersItemsSplitPct';

function BrandIcon({ type }) {
  const t = String(type || '').toLowerCase();
  if (t.includes('shopify'))     return <SiShopify className="integration-icon shopify" title="Shopify" />;
  if (t.includes('amazon'))      return <SiAmazon  className="integration-icon amazon" title="Amazon" />;
  if (t.includes('ebay'))        return <SiEbay    className="integration-icon ebay" title="Ebay" />;
  if (t.includes('walmart'))     return <SiWalmart className="integration-icon walmart" title="Walmart" />;
  if (t.includes('woo'))         return <SiWoocommerce className="integration-icon woocommerce" title="WooCommerce" />;
  return <FaStore className="integration-icon generic" title="Integration" />;
}

function getUserFullName() {
  try {
    const u = JSON.parse(localStorage.getItem('currentUser')) ||
              JSON.parse(localStorage.getItem('user')) || {};
    return u.fullname || u.userFullName || u.fullName || u.name || u.login || '';
  } catch {
    return '';
  }
}

/** Lightweight searchable multi-select (status filter) */
function MultiSelectStatuses({ options = [], value = [], onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  const [popStyle, setPopStyle] = useState({ top: 0, left: 0, width: 320 });

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, []);

  // Reposition popover using viewport coordinates to avoid clipping by overflow parents
  useEffect(() => {
    if (!open) return;
    const computePos = () => {
      const MARGIN = 10;
      const anchor = ref.current;
      if (!anchor) return;
      const r = anchor.getBoundingClientRect();
      const width = Math.max(200, Math.min(320, window.innerWidth - MARGIN * 2));

      let left = r.left; // default align to left
      if (left + width > window.innerWidth - MARGIN) {
        left = Math.max(MARGIN, r.right - width); // align to right if needed
      }
      const estimatedHeight = 320; // rough max height
      const top = Math.min(r.bottom + 6, Math.max(MARGIN, window.innerHeight - estimatedHeight - MARGIN));
      setPopStyle({ top, left, width });
    };
    computePos();
    window.addEventListener('resize', computePos);
    window.addEventListener('scroll', computePos, true);
    return () => {
      window.removeEventListener('resize', computePos);
      window.removeEventListener('scroll', computePos, true);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options;
    return options.filter(o => (o.name || '').toLowerCase().includes(s));
  }, [q, options]);

  const isAll = value.length === 0;
  const toggle = (id) => {
    const sid = String(id);
    if (value.includes(sid)) onChange(value.filter(v => v !== sid));
    else onChange([...value, sid]);
  };
  const clear = (e) => { e?.stopPropagation?.(); onChange([]); };
  const selectAll = () => onChange(options.map(o => String(o.id)));

  const selectedNames = options.filter(o => value.includes(String(o.id))).map(o => o.name);
  const summary = isAll ? 'All statuses'
    : selectedNames.length <= 2 ? selectedNames.join(', ')
    : `${selectedNames.slice(0,2).join(', ')} +${selectedNames.length - 2}`;

  return (
    <div className="ms-container" ref={ref}>
      <button
        type="button"
        className={`ms-control ${open ? 'open' : ''}`}
        onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Filter by Our Status"
      >
        <span className={`ms-summary ${isAll ? 'muted' : ''}`}>{summary}</span>
        {!isAll && (
          <span className="ms-clear" onClick={clear} title="Clear" aria-label="Clear">
            <FaTimes size={12}/>
          </span>
        )}
        <FaChevronDown size={14} className="ms-caret" />
      </button>

      {open && (
        <div className="ms-popover fixed" style={{ position: 'fixed', top: popStyle.top, left: popStyle.left, width: popStyle.width }}>
          <div className="ms-tools">
            <input
              className="ms-search"
              type="text"
              placeholder="Search statuses…"
              value={q}
              onChange={e => setQ(e.target.value)}
              autoFocus
            />
            <div className="ms-actions">
              <button type="button" className="mini-btn" onClick={selectAll}>Select all</button>
              <button type="button" className="mini-btn" onClick={clear}>Clear</button>
            </div>
          </div>
          <ul className="ms-list" role="listbox" aria-multiselectable="true">
            {filtered.length ? filtered.map(o => {
              const checked = value.includes(String(o.id));
              return (
                <li key={o.id}>
                  <label className="ms-option">
                    <input type="checkbox" checked={checked} onChange={() => toggle(o.id)} />
                    <span>{o.name}</span>
                  </label>
                </li>
              );
            }) : <li className="ms-empty">No matches</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function EOrders() {
  const [confirmState, setConfirmState] = useState({
    open: false,
    title: 'Confirm',
    message: '',
    confirmText: 'Yes',
    cancelText: 'Cancel',
    onConfirm: null,
  });
  const openConfirm = (message, onConfirm, opts = {}) => {
    const { title = 'Confirm', confirmText = 'Yes', cancelText = 'Cancel' } = opts || {};
    setConfirmState({ open: true, title, message, confirmText, cancelText, onConfirm });
  };
  const closeConfirm = () => setConfirmState({
    open: false, title: 'Confirm', message: '', confirmText: 'Yes', cancelText: 'Cancel', onConfirm: null
  });

  // Notice modal (success/error)
  const [notify, setNotify] = useState({ open: false, type: 'success', title: '', message: '' });
  const openNotify = (type, title, message) => setNotify({ open: true, type, title, message });
  const closeNotify = () => setNotify(n => ({ ...n, open: false }));

  const [orders, setOrders] = useState([]);
  const pendingScrollIdRef = useRef(null); // ensure row is visible after closing editor or refresh
  // Resizable columns for E-Orders list
  const EORD_COLS = ['select','actions','clientname','ordernumber','altordernumber','id','orderdate','assignedname','ourstatusname','orderstatus','shipto','shipaddress','shippingmethod','tracking','notes'];
  const LS_EORD_COLS = 'eordersColWidths.v1';
  const getInitialEordWidths = () => {
    try { const raw = localStorage.getItem(LS_EORD_COLS); const j = raw ? JSON.parse(raw) : {}; return (j && typeof j === 'object') ? j : {}; }
    catch { return {}; }
  };
  const [eordColWidths, setEordColWidths] = useState(getInitialEordWidths());
  const eordHeaderRef = useRef(null);
  const eordTableRef = useRef(null);
  const eordListColRef = useRef(null); // scroll container for the table
  const eordResizeRef = useRef({ key: null, startX: 0, startW: 0 });
  const eordResizedRecently = useRef(false);
  useEffect(() => { try { localStorage.setItem(LS_EORD_COLS, JSON.stringify(eordColWidths)); } catch {} }, [eordColWidths]);
  const ensureEordWidthsLocked = () => {
    const row = eordHeaderRef.current; if (!row) return;
    const ths = Array.from(row.children || []);
    if (!ths.length) return;
    // Only lock if any key is missing
    const haveAll = EORD_COLS.every(k => eordColWidths && eordColWidths[k] != null);
    if (haveAll) return;
    const next = {};
    EORD_COLS.forEach((k, i) => {
      const th = ths[i];
      if (th) next[k] = th.offsetWidth;
    });
    setEordColWidths(next);
  };
  const onEordMove = (e) => {
    const { key, startX, startW } = eordResizeRef.current; if (!key) return;
    const nextW = Math.max(MIN_COL_W, startW + (e.clientX - startX));
    setEordColWidths(prev => ({ ...prev, [key]: nextW }));
    e.preventDefault();
  };
  const onEordUp = () => {
    eordResizeRef.current = { key: null, startX: 0, startW: 0 };
    try { document.body.classList.remove('col-resizing'); } catch {}
    window.removeEventListener('mousemove', onEordMove);
    window.removeEventListener('mouseup', onEordUp);
    // Briefly suppress sort clicks after resize to avoid accidental sorts
    eordResizedRecently.current = true;
    setTimeout(() => { eordResizedRecently.current = false; }, 150);
  };
  const startEordResize = (key, e) => {
    ensureEordWidthsLocked();
    const th = e.currentTarget.parentElement;
    eordResizeRef.current = { key, startX: e.clientX, startW: th.offsetWidth };
    try { document.body.classList.add('col-resizing'); } catch {}
    window.addEventListener('mousemove', onEordMove);
    window.addEventListener('mouseup', onEordUp);
    e.preventDefault(); e.stopPropagation();
  };
  const autoFitEordCol = (key) => {
    try {
      const idx = EORD_COLS.indexOf(key); if (idx === -1) return;
      let maxW = 0;
      const hdr = eordHeaderRef.current?.children?.[idx]; if (hdr) maxW = Math.max(maxW, Math.ceil(hdr.scrollWidth));
      const tbl = eordTableRef.current; if (tbl?.tBodies?.[0]) {
        const rows = tbl.tBodies[0].rows; for (let r = 0; r < rows.length; r++) { const cell = rows[r].children[idx]; if (cell) maxW = Math.max(maxW, Math.ceil(cell.scrollWidth)); }
      }
      const target = Math.max(MIN_COL_W, Math.min(800, maxW));
      setEordColWidths(prev => ({ ...prev, [key]: target }));
    } catch {}
  };

  // (moved below after sort fields are initialized)

  // ===== Resizable columns for E-Orders list =====
  
  const [calcOpen, setCalcOpen] = useState(false);
  const [newOpen, setNewOpen]   = useState(false);

  const [syncing, setSyncing] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncData, setSyncData] = useState(null);
  // Import CSV (E-Orders)
  const eorderImportInputRef = useRef(null);
  const [eordImportLoading, setEordImportLoading] = useState(false);

  const [syncingNowName, setSyncingNowName] = useState('');
  const [shopifyProgress, setShopifyProgress] = useState(null);
  const syncStatusTimerRef = useRef(null);

  const [limit, setLimit] = useState(getInitialLimit());
  const [limitInput, setLimitInput] = useState(String(getInitialLimit()));
  const [meta, setMeta] = useState({ total: 0, limit: getInitialLimit(), offset: 0 });
  // Assign to user modal state
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignFor, setAssignFor] = useState(null);
  const [assignUsers, setAssignUsers] = useState([]);
  const [assignUserId, setAssignUserId] = useState('');

  const openAssignModal = async (order) => {
    try {
      const r = await fetch(`${API}/api/eorders/internal-users`);
      const j = await r.json().catch(()=>[]);
      setAssignUsers(Array.isArray(j) ? j : []);
    } catch { setAssignUsers([]); }
    setAssignUserId('');
    setAssignFor(order);
    setAssignOpen(true);
  };
  const closeAssignModal = () => { setAssignOpen(false); setAssignFor(null); setAssignUserId(''); };
  const doAssignTo = async () => {
    if (!assignFor?.id || !assignUserId) return;
    try {
      const res = await fetch(`${API}/api/eorders/${assignFor.id}/assign`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userid: Number(assignUserId) }) });
      const j = await res.json();
      if (!res.ok || j?.error) throw new Error(j?.error || 'Failed');
      const name = j.assignedname || (assignUsers.find(u => String(u.userid) === String(assignUserId))?.fullname || '');
      setOrders(prev => prev.map(o => (o.id === assignFor.id ? { ...o, assignedto: Number(assignUserId), assignedname: name } : o)));
      closeAssignModal();
    } catch (e) {
      openNotify('error', 'Assign Failed', e?.message || 'Failed to assign');
    }
  };

  // Bulk Unassign helper (used in More… menu)
  const bulkUnassign = async () => {
    if (!selectedIds.size) { openNotify('warn','No Selection','Select at least one e-order.'); return; }
    try {
      const body = { ids: Array.from(selectedIds) };
      const res = await fetch(`${API}/api/eorders/bulk/unassign`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const j = await res.json();
      if (!res.ok || j?.error) throw new Error(j?.error||'Failed');
      setOrders(prev => prev.map(o => (selectedIds.has(o.id) ? { ...o, assignedto: null, assignedname: '' } : o)));
      showToast({ id: 'eorders.bulk.unassign', message: `Unassigned ${selectedIds.size} order${selectedIds.size===1?'':'s'}.`, duration: 2000, align: 'center', slideFrom: 'top', pill: true });
    } catch (e) {
      openNotify('error','Bulk Unassign Failed', e?.message || 'Failed');
    }
  };

  // "More…" toolbar menu state and popover placement
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const [moreStyle, setMoreStyle] = useState({ top: 0, left: 0, width: 240 });
  useEffect(() => {
    const onDoc = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setMoreOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, []);
  useEffect(() => {
    if (!moreOpen) return;
    const computePos = () => {
      const MARGIN = 10;
      const anchor = moreRef.current;
      if (!anchor) return;
      const r = anchor.getBoundingClientRect();
      const width = Math.max(220, Math.min(320, window.innerWidth - MARGIN * 2));
      let left = r.left;
      if (left + width > window.innerWidth - MARGIN) left = Math.max(MARGIN, r.right - width);
      const estimatedHeight = 360;
      const top = Math.min(r.bottom + 6, Math.max(MARGIN, window.innerHeight - estimatedHeight - MARGIN));
      setMoreStyle({ top, left, width });
    };
    computePos();
    window.addEventListener('resize', computePos);
    window.addEventListener('scroll', computePos, true);
    return () => { window.removeEventListener('resize', computePos); window.removeEventListener('scroll', computePos, true); };
  }, [moreOpen]);

  // Filters with persistence
  const [q, setQ] = useState(getLS(EO_K_Q, ''));
  const initSort = getInitialSort();
  const [sortField, setSortField] = useState(initSort.sortField);
  const [sortOrder, setSortOrder] = useState(initSort.sortOrder);

  const [fromDate, setFromDate] = useState(getLS(EO_K_FROM, ''));
  const [toDate, setToDate]     = useState(getLS(EO_K_TO, ''));
  const [datePreset, setDatePreset] = useState(localStorage.getItem(LS_DATE_PRESET) || getLS(EO_K_PRESET, ''));

  const [clientOptions, setClientOptions] = useState([]);
  const [clientFilter, setClientFilter] = useState(getLS(EO_K_CLIENTNAME, ''));
  const [filteredClientOptions, setFilteredClientOptions] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(getLS(EO_K_CLIENTID, ''));
  const [clientNameQuery, setClientNameQuery] = useState(getLS(EO_K_CLIENTNAME, ''));

  // Lock widths once rows render or sorting changes (post-paint)
  useEffect(() => {
    const t = setTimeout(() => ensureEordWidthsLocked(), 0);
    return () => clearTimeout(t);
  }, [orders, sortField, sortOrder]);

  const [serviceTypes, setServiceTypes] = useState([]);
  const [storeStatuses, setStoreStatuses] = useState([]);
  const [ourStatuses, setOurStatuses] = useState([]);

  function getUserFullName() {
    try {
      const u = JSON.parse(localStorage.getItem('currentUser')) || JSON.parse(localStorage.getItem('user')) || {};
      return (u.fullname || u.userFullName || u.fullName || u.name || u.login || '');
    } catch { return ''; }
  }

  const createInvoiceForEOrder = async (eorderId, ordernumber = null) => {
    if (!eorderId) return;
    try {
      const res = await fetch(`${API}/api/invoices/from-eorder/${eorderId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userFullName: getUserFullName() })
      });
      const j = await res.json();
      if (!res.ok || !j?.id) throw new Error(j?.error || 'Failed to create invoice');
      await fetchOrders(meta.offset, limit);
      const displayNum = ordernumber || eorderId;
      openNotify('success', 'Invoice Created', `Invoice #${j.id} created for E-Order #${displayNum}.`);
    } catch (e) {
      openNotify('error', 'Invoice Creation Failed', e.message);
    }
  };

  const [serviceTypeFilter, setServiceTypeFilter] = useState(getLS(EO_K_SERVICETYPE, ''));
  const [storeStatusFilter, setStoreStatusFilter] = useState(getLS(EO_K_STORESTATUS, ''));
  const [billedFilter, setBilledFilter] = useState(getLS(EO_K_BILLED, ''));
  useEffect(() => { try { localStorage.setItem(EO_K_BILLED, billedFilter || ''); } catch {} }, [billedFilter]);

  // NEW: multi-select our statuses
  const initialOurStatusIds = getLS(EO_K_OURSTATUS_IDS, '');
  const [ourStatusIds, setOurStatusIds] = useState(
    initialOurStatusIds ? initialOurStatusIds.split(',').filter(Boolean) : []
  );

  // splitter vertical sizes
  const getInitialEorderSplitHeights = () => {
    try {
      const saved = localStorage.getItem(EORD_SPLIT_KEY);
      if (saved) {
        const arr = JSON.parse(saved);
        if (Array.isArray(arr) && arr.length === 2) return arr;
      }
      const pct = parseInt(localStorage.getItem(EORD_SPLIT_PCT) || '', 10);
      if (!Number.isNaN(pct)) return [100 - pct, pct];
    } catch {}
    return [65, 35];
  };
  const [eoSplitHeights, setEoSplitHeights] = useState(getInitialEorderSplitHeights);

  const [statusOptions, setStatusOptions] = useState([]);
  const [refreshingIds, setRefreshingIds] = useState(new Set());
  const [rowToasts, setRowToasts] = useState({}); // { [orderId]: { message, ts } }

  const pushRowToast = (orderId, message, ms = 2000) => {
    setRowToasts(prev => ({ ...prev, [orderId]: { message, ts: Date.now() } }));
    setTimeout(() => {
      setRowToasts(prev => {
        const next = { ...prev };
        if (next[orderId]) delete next[orderId];
        return next;
      });
    }, Math.max(500, ms));
  };

  // Refresh a single E-Order's allocation/status (component-scoped)
  const refreshEOrder = async (order) => {
    if (!order?.id) return;
    setRefreshingIds(prev => { const s = new Set(prev); s.add(order.id); return s; });
    try {
      const res = await fetch(`${API}/api/eorders/${order.id}/refresh`, { method: 'POST' });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j?.success === false) throw new Error(j?.error || 'Refresh failed');

      const alloc = j?.allocation || {};
      const didAlloc = Number(alloc.allocatedPieces || 0) > 0;
      if (didAlloc) {
        pushRowToast(order.id, `Allocated ${alloc.allocatedPieces} pcs${alloc.pending ? ' (back order remains)' : ''}`);
      } else {
        const statusName = String(order.ourstatusname || '').toUpperCase().replace(/\s+/g, ' ').trim();
        const SKIP_WARN = new Set(['READY TO SHIP','SHIPPED','IN ROUTE','DELIVERED']);
        if (!SKIP_WARN.has(statusName)) {
          pushRowToast(order.id, 'No available inventory to allocate');
        }
      }

      const tr = j?.tracking || {};
      const didTrackChange = !!tr?.changed;
      if (didTrackChange) pushRowToast(order.id, 'Status updated from carrier');
      // If neither allocation nor tracking changed, show a light info toast
      if (!didAlloc && !didTrackChange) {
        pushRowToast(order.id, 'No changes detected');
      }

      let freshRow = null;
      try {
        freshRow = await fetchJSON(`${API}/api/eorders/${order.id}`);
      } catch (reloadErr) {
        try { console.warn('Failed to reload e-order row after refresh', reloadErr); } catch {}
      }

      // Update the matching row in-place rather than reloading the whole list
      setOrders(prev => prev.map(o => {
        if (o.id !== order.id) return o;
        if (freshRow) {
          return { ...o, ...freshRow };
        }
        const next = { ...o };
        if (typeof tr.our_order_status === 'number') {
          next.our_order_status = tr.our_order_status;
          if (tr.anyDelivered) next.ourstatusname = 'DELIVERED';
          else if (tr.anyInTransit) next.ourstatusname = 'IN ROUTE';
        } else {
          if (tr.anyDelivered) next.ourstatusname = 'DELIVERED';
          else if (tr.anyInTransit) next.ourstatusname = 'IN ROUTE';
        }
        return next;
      }));

      if (freshRow && selectedEOrderId === order.id) {
        setSelectedEOrderMeta({ clientname: freshRow.clientname || '', ordernumber: freshRow.ordernumber || '' });
      }
    } catch (e) {
      openNotify('error', 'Refresh Failed', e.message || 'Refresh failed');
    } finally {
      setRefreshingIds(prev => { const s = new Set(prev); s.delete(order.id); return s; });
    }
  };
  const [showDeleted, setShowDeleted] = useState(getLS(EO_K_SHOWDELETED, '0') === '1');

  // Selection
const [selectedIds, setSelectedIds] = useState(new Set());
const allSelected = orders.length > 0 && selectedIds.size === orders.length;

  const [loadingList, setLoadingList] = useState(false);
  // Centered topbar toast; on completion, report count and keep a moment
  const prevLoadingRef = useRef(false);
  useEffect(() => {
    const id = 'loading.eorders';
    if (loadingList && !prevLoadingRef.current) {
      showToast({ id, message: 'Loading eOrders…', duration: 2500, align: 'center', slideFrom: 'top', pill: true });
    }
    if (!loadingList && prevLoadingRef.current) {
      const total = Number((meta && meta.total) || 0);
      const count = Number.isFinite(total) && total > 0 ? total : (Array.isArray(orders) ? orders.length : 0);
      const formatted = Number(count).toLocaleString('en-US');
      // Re-show with same id so ToastHost dedupes and resets timer reliably
      showToast({ id, message: `Loaded ${formatted} eOrders.`, duration: 2500, align: 'center', slideFrom: 'top', pill: true });
    }
    prevLoadingRef.current = loadingList;
  }, [loadingList, orders, meta?.total]);
  const [listProgress, setListProgress] = useState(0);

  // Lightweight progress bar while refreshing the list
  useEffect(() => {
    if (!loadingList) {
      if (listProgress > 0 && listProgress < 100) setListProgress(100);
      const t = setTimeout(() => setListProgress(0), 250); // allow bar to complete then hide
      return () => clearTimeout(t);
    }
    // start at 20% and slowly advance up to 90% until complete
    setListProgress(p => (p > 0 ? p : 20));
    const id = setInterval(() => {
      setListProgress(prev => {
        if (!loadingList) return prev;
        const next = prev + Math.random() * 10; // jittered advance
        return Math.min(90, next);
      });
    }, 200);
    return () => clearInterval(id);
  }, [loadingList]);

  // Map of orderId -> array of tracking numbers
  const [trackingById, setTrackingById] = useState({});
  // Fetch tracking numbers for any orders on the current page (not just SHIPPED)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!Array.isArray(orders) || !orders.length) return;
      const candidateIds = orders
        .map(o => Number(o.id))
        .filter(id => Number.isFinite(id) && !(String(id) in trackingById));

      if (!candidateIds.length) return;

      const entries = await Promise.all(candidateIds.map(async (id) => {
        try {
          const res = await fetch(`${API}/api/eorders/${id}/shipments`);
          if (!res.ok) return [id, []];
          const rows = await res.json().catch(() => []);
          // Keep number + carrier; ensure unique by number
          const uniq = new Map();
          for (const r of (rows || [])) {
            const num = String(r?.trackingnumber || '').trim();
            if (!num) continue;
            if (!uniq.has(num)) uniq.set(num, { num, carrier: r?.carrier || '' });
          }
          return [id, Array.from(uniq.values())];
        } catch {
          return [id, []];
        }
      }));

      if (cancelled) return;
      const patch = {};
      for (const [id, items] of entries) {
        if (items && items.length) patch[String(id)] = items;
      }
      if (Object.keys(patch).length) {
        setTrackingById(prev => ({ ...prev, ...patch }));
      }
    })();
    return () => { cancelled = true; };
  }, [orders]);  // intentionally not depending on trackingById to avoid refetch loops

  // Editor state
const [selected, setSelected] = useState(null);
const [editing, setEditing] = useState(false);
const [creating, setCreating] = useState(false);
const [saving, setSaving] = useState(false);
const [selectedTrackingNums, setSelectedTrackingNums] = useState([]);
const [selectedShipments, setSelectedShipments] = useState([]);
const [trackingInput, setTrackingInput] = useState('');
const [savingMetaTracking, setSavingMetaTracking] = useState(false);
const [splitModalOpen, setSplitModalOpen] = useState(false);
const [splitLines, setSplitLines] = useState([]);
const [splitLinesLoading, setSplitLinesLoading] = useState(false);
const [splitSelectedLines, setSplitSelectedLines] = useState(new Set());
const [splitQuantities, setSplitQuantities] = useState({});
const [splitError, setSplitError] = useState('');
const [splitBusy, setSplitBusy] = useState(false);
const [duplicateBusy, setDuplicateBusy] = useState(false);

const [selectedEOrderId, setSelectedEOrderId] = useState(null);
const [selectedEOrderMeta, setSelectedEOrderMeta] = useState({ clientname: '', ordernumber: '' });

// Determine if Buy Shipping / Split are allowed
const selectedOneId = useMemo(() => (selectedIds.size === 1 ? Array.from(selectedIds)[0] : null), [selectedIds]);
const selectedOrder = useMemo(() => (selectedOneId ? orders.find(o => o.id === selectedOneId) : null), [selectedOneId, orders]);
const canBuyShipping = !!(selectedOrder && Number(selectedOrder.our_order_status || 0) < 6);
const canSplitOrder = selectedIds.size === 1;
const splitOrderLabel = selectedOrder?.ordernumber || selectedEOrderMeta.ordernumber || (selectedOneId ? String(selectedOneId) : '');
const getLineAvailableQty = (line) => {
  if (!line) return 0;
  const ordered = Number(line.qtyOrdered ?? line.qty_ordered ?? line.qty ?? 0);
  const allocated = Number(line.qtyAllocated ?? line.qty_allocated ?? 0);
  return Math.max(0, ordered - allocated);
};

  // Auto-track statuses always on; removed toggle and persistence

  // Load tracking numbers for the selected order when editing
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!editing || !selected?.id) { setSelectedTrackingNums([]); return; }
      try {
        const res = await fetch(`${API}/api/eorders/${selected.id}/shipments`);
        if (!res.ok) { setSelectedShipments([]); setSelectedTrackingNums([]); setTrackingInput(''); return; }
        const rows = await res.json().catch(() => []);
        if (!cancelled) {
          setSelectedShipments(Array.isArray(rows) ? rows : []);
          const nums = Array.from(new Set((rows || []).map(r => String(r.trackingnumber || '').trim()).filter(Boolean)));
          setSelectedTrackingNums(nums);
          setTrackingInput(nums.length ? nums.join(', ') : '');
        }
      } catch {
        if (!cancelled) { setSelectedShipments([]); setSelectedTrackingNums([]); setTrackingInput(''); }
      }
    })();
    return () => { cancelled = true; };
  }, [editing, selected?.id]);

  useEffect(() => {
    if (!splitModalOpen) return;
    if (!selectedOneId) {
      setSplitModalOpen(false);
      return;
    }
    let cancelled = false;
    setSplitLines([]);
    setSplitSelectedLines(new Set());
    setSplitQuantities({});
    setSplitError('');
    setSplitLinesLoading(true);
    (async () => {
      try {
        const rows = await fetchJSON(`${API}/api/eorders/${selectedOneId}/items?deleted=0`);
        if (cancelled) return;
        const dedup = [];
        const seen = new Set();
        (Array.isArray(rows) ? rows : []).forEach((row) => {
          const lineId = Number(row?.parent_line_id || row?.id);
          if (!lineId || seen.has(lineId)) return;
          seen.add(lineId);
          const qtyOrdered = Number(row?.qty_ordered || 0);
          const qtyAllocated = Number(row?.qty_allocated || 0);
          dedup.push({
            id: lineId,
            sku: row?.sku || '',
            description: row?.itemdescription || '',
            qtyOrdered,
            qtyAllocated,
            qtyShipped: Number(row?.qty_shipped || 0),
            availableQty: Math.max(0, qtyOrdered - qtyAllocated),
          });
        });
        setSplitLines(dedup);
        setSplitQuantities(() => {
          const next = {};
          dedup.forEach((line) => {
            next[line.id] = 0;
          });
          return next;
        });
      } catch (err) {
        if (!cancelled) setSplitError(err.message || 'Failed to load line items.');
      } finally {
        if (!cancelled) setSplitLinesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [splitModalOpen, selectedOneId]);

useEffect(() => {
  if (splitModalOpen && selectedIds.size !== 1 && !splitBusy) {
    setSplitModalOpen(false);
    setSplitLines([]);
    setSplitSelectedLines(new Set());
    setSplitQuantities({});
    setSplitLinesLoading(false);
    setSplitError('');
  }
}, [splitModalOpen, selectedIds, splitBusy]);

  const saveMetaTracking = async () => {
    if (!editing || !selected?.id) return;
    const nums = String(trackingInput || '').split(',').map(s => s.trim()).filter(Boolean);
    const rows = Array.isArray(selectedShipments) ? selectedShipments.filter(r => Number(r.labelid)) : [];
    if (!rows.length) {
      // no labels to update; nothing to do (keep input editable for future labels)
      return;
    }
    try {
      setSavingMetaTracking(true);
      const pairs = [];
      if (rows.length === 1) {
        if (nums.length >= 1) pairs.push([rows[0].labelid, nums[0]]);
      } else {
        const n = Math.min(nums.length, rows.length);
        for (let i = 0; i < n; i++) pairs.push([rows[i].labelid, nums[i]]);
      }
      await Promise.all(pairs.map(async ([labelId, num]) => {
        const res = await fetch(`${API}/api/eorders/${selected.id}/labels/${labelId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ trackingnumber: num })
        });
        if (!res.ok) {
          let msg = `Failed to update label ${labelId}`;
          try { const j = await res.json(); if (j?.error) msg = j.error; } catch {}
          throw new Error(msg);
        }
      }));
      // Refresh shipments list and tracking arrays after save
      try {
        const res = await fetch(`${API}/api/eorders/${selected.id}/shipments`);
        const rowsNew = res.ok ? await res.json() : [];
        setSelectedShipments(Array.isArray(rowsNew) ? rowsNew : []);
        const numsNew = Array.from(new Set((rowsNew || []).map(r => String(r.trackingnumber || '').trim()).filter(Boolean)));
        setSelectedTrackingNums(numsNew);
        setTrackingInput(numsNew.length ? numsNew.join(', ') : '');
      } catch {}
    } finally {
      setSavingMetaTracking(false);
    }
  };

  // ===== Lightweight background scheduler: carrier status refresh =====
  const ordersRef = useRef([]);
  useEffect(() => { ordersRef.current = orders; }, [orders]);
  const busyRefreshRef = useRef(false);
  const lastIdxRef = useRef(0);
  const carrierTimerRef = useRef(null);
  const carrierWarmRef = useRef(null);
  const carrierOnVisRef = useRef(null);

  const STATUS_TO_TRACK = useMemo(() => new Set(['READY TO SHIP','SHIPPED','IN ROUTE']), []);
  const IS_ADMIN = useMemo(() => isAdminUser(), []);

  const carrierTick = async () => {
    if (document.hidden) return;           // pause when not visible
    if (busyRefreshRef.current) return;    // avoid overlap
    const list = ordersRef.current || [];
    if (!Array.isArray(list) || list.length === 0) return;

    // Filter candidates on current page
    const cand = list
      .filter(o => STATUS_TO_TRACK.has(String(o.ourstatusname || '')
        .toUpperCase()
        .replace(/\s+/g, ' ')
        .trim()))
      .map(o => Number(o.id))
      .filter(id => Number.isFinite(id) && id > 0);
    if (!cand.length) return;

    // Cycle through a few per tick to reduce load
    const MAX_PER_TICK = 4;
    const out = [];
    for (let n = 0; n < Math.min(MAX_PER_TICK, cand.length); n++) {
      const idx = (lastIdxRef.current + n) % cand.length;
      out.push(cand[idx]);
    }
    lastIdxRef.current = (lastIdxRef.current + out.length) % cand.length;

    busyRefreshRef.current = true;
    try { window.dispatchEvent(new CustomEvent('topbar:setStatus', { detail: { status: 'Syncing shipping statuses…' } })); } catch {}
    try {
      await Promise.all(out.map(async (id) => {
        try { await fetch(`${API}/api/eorders/${id}/refresh`, { method: 'POST' }); } catch {}
      }));
      // Friendly toast so users know the timer ran
      if (out.length > 0) {
        const msg = `Refreshed ${out.length} order${out.length === 1 ? '' : 's'}.`;
        showToast({ id: 'eorders.carrier.refresh', message: msg, duration: 2000, align: 'center', slideFrom: 'top', pill: true });
      }
      // Do not refresh the entire list on every carrier tick; the list auto-refresh
      // uses the configured interval (in minutes) separately, and manual actions
      // will refresh the specific order when needed.
    } finally {
      busyRefreshRef.current = false;
      try { window.dispatchEvent(new CustomEvent('topbar:clearStatus')); } catch {}
    }
  };

  const configureCarrierTimer = (sec) => {
    // Cleanup any existing timers/listeners
    try { if (carrierTimerRef.current) clearInterval(carrierTimerRef.current); } catch {}
    try { if (carrierWarmRef.current) clearTimeout(carrierWarmRef.current); } catch {}
    if (carrierOnVisRef.current) {
      document.removeEventListener('visibilitychange', carrierOnVisRef.current);
      carrierOnVisRef.current = null;
    }
    carrierTimerRef.current = null;
    carrierWarmRef.current = null;

    const seconds = Math.max(0, Number(sec || 0));
    if (seconds <= 0) return; // disabled
    const ms = Math.max(5000, seconds * 1000); // safety minimum
    carrierTimerRef.current = setInterval(carrierTick, ms);
    carrierWarmRef.current = setTimeout(carrierTick, 10 * 1000);
    carrierOnVisRef.current = () => { if (!document.hidden) setTimeout(carrierTick, 200); };
    document.addEventListener('visibilitychange', carrierOnVisRef.current);
  };

  useEffect(() => {
    configureCarrierTimer(getRefreshSeconds());
    const onChanged = (e) => configureCarrierTimer(e?.detail?.seconds ?? getRefreshSeconds());
    window.addEventListener('eorders:refreshIntervalChanged', onChanged);
    return () => {
      window.removeEventListener('eorders:refreshIntervalChanged', onChanged);
      configureCarrierTimer(0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build a public tracking URL (borrowed from Shipments.jsx)
  function buildTrackingUrl(rawCarrier, tracking) {
    const tn = String(tracking || '').trim();
    if (!tn) return null;
    const norm = String(rawCarrier || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    const is = (needle) => norm.includes(needle);
    if (is('ups'))   return `https://www.ups.com/track?loc=en_US&tracknum=${encodeURIComponent(tn)}`;
    if (is('fedex') || is('fx')) return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(tn)}`;
    if (is('usps') || is('postal service') || is('post office')) return `https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${encodeURIComponent(tn)}`;
    if (is('dhl') && !is('ecommerce')) return `https://www.dhl.com/global-en/home/tracking/tracking-express.html?tracking-id=${encodeURIComponent(tn)}`;
    if (is('dhl ecommerce') || is('dhl global mail') || is('ecommerce')) return `https://www.dhl.com/us-en/home/tracking/tracking-ecommerce.html?submit=1&tracking-id=${encodeURIComponent(tn)}`;
    if (is('ontrac')) return `https://www.ontrac.com/tracking/?number=${encodeURIComponent(tn)}`;
    if (is('amazon') || is('amzl')) return `https://www.17track.net/en/track?nums=${encodeURIComponent(tn)}`;
    if (is('canada post') || is('canadapost')) return `https://www.canadapost-postescanada.ca/track-reperage/en#/search?searchFor=${encodeURIComponent(tn)}`;
    if (is('royal mail')) return `https://www.royalmail.com/track-your-item#/tracking-results/${encodeURIComponent(tn)}`;
    if (is('australia post') || is('auspost') || is('australiapost')) return `https://auspost.com.au/mypost/track/#/details/${encodeURIComponent(tn)}`;
    if (is('evri') || is('hermes')) return `https://www.evri.com/track/parcel/${encodeURIComponent(tn)}`;
    if (is('gls')) return `https://gls-group.com/US/en/parcel-tracking?match=${encodeURIComponent(tn)}`;
    if (is('lasership')) return `https://www.lasership.com/track/${encodeURIComponent(tn)}`;
    // Fallback multi-carrier
    return `https://www.17track.net/en/track?nums=${encodeURIComponent(tn)}`;
  }

const IS_CLIENT = useMemo(() => isClientUser(), []);
const CLIENT_ID = useMemo(() => getLoggedInClientId(), []);
const singleClientOption = useMemo(
  () => (IS_CLIENT && clientOptions.length === 1 ? clientOptions[0] : null),
  [IS_CLIENT, clientOptions]
);

// Items split view
// Bottom pane mode + data (Items | History | Attachments)
  const [eoBottomPaneMode, setEoBottomPaneMode] = useState(null);
  const [eoHistory, setEoHistory] = useState([]);
  const [eoHistoryLoading, setEoHistoryLoading] = useState(false);
  const [eoAttachments, setEoAttachments] = useState([]);
  const [eoAttachmentsLoading, setEoAttachmentsLoading] = useState(false);
  const [eoAttachDesc, setEoAttachDesc] = useState('');
  const [eoPendingFiles, setEoPendingFiles] = useState([]);
  const [eoDragging, setEoDragging] = useState(false);
  const eoFileInputRef = useRef(null);

  // Detail drawer (view only)
  const [detail, setDetail] = useState(null);

  // Label modal (print labels)
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [labelOrderId, setLabelOrderId] = useState(null);

  // Picker modal (Add from Inventory – deducts)
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerFor, setPickerFor]   = useState(null);

  // Abort/race control for list fetch
  const eordersFetchSeq = useRef(0);
  const eordersFetchAbort = useRef(null);

  // -------- fetch helpers ----------
  const fetchJSON = async (url, opts = {}) => {
    const res = await fetch(url, opts);
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try { const b = await res.json(); if (b?.error) msg = b.error; } catch {}
      throw new Error(msg);
    }
    return res.json();
  };

  const fetchClientOptions = async () => {
    try {
      if (IS_CLIENT) {
        const res = await fetch(`${API}/api/reports/filters/clients`);
        const data = res.ok ? await res.json() : [];
        const opts = (Array.isArray(data) ? data : []).map(c => ({ id: String(c.id), name: c.name }));
        setClientOptions(opts);
        setFilteredClientOptions(opts);
        // keep selectedClientId only if valid
        if (selectedClientId && !opts.find(o => String(o.id) === String(selectedClientId))) {
          setSelectedClientId('');
        }
        if (opts.length === 1) {
          setSelectedClientId(String(opts[0].id));
          setClientNameQuery('');
        }
        return;
      }
      const json = await fetchJSON(`${API}/api/clients?limit=1000`);
      const opts = Array.isArray(json.data)
        ? json.data.map(c => ({ id: c.id, name: c.name }))
        : [];
      setClientOptions(opts);
      setFilteredClientOptions(opts);
    } catch {
      setClientOptions([]);
      setFilteredClientOptions([]);
    }
  };

  const fetchMetaFilters = async () => {
    try {
      const j = await fetchJSON(`${API}/api/eorders/meta`);
      setServiceTypes(Array.isArray(j.servicetypes) ? j.servicetypes : []);
      setStoreStatuses(Array.isArray(j.storestatuses) ? j.storestatuses : []);
      const ours = Array.isArray(j.ourstatuses) ? j.ourstatuses : [];
      setOurStatuses([...ours].sort((a,b)=>statusIndex(a.name)-statusIndex(b.name)));
    } catch {
      setServiceTypes([]);
      setStoreStatuses([]);
      setOurStatuses([]);
    }
  };

  const fetchStatusOptions = async () => {
    try {
      const json = await fetchJSON(`${API}/api/eorders/statuses`);
      const sorted = Array.isArray(json) ? [...json].sort((a,b)=>statusIndex(a.name)-statusIndex(b.name)) : [];
      setStatusOptions(sorted);
    } catch {
      setStatusOptions([]);
    }
  };

  useEffect(() => {
    if (!IS_CLIENT) {
      // Clear any persisted client id only when there is no manual client filter
      const hasManualClientFilter = !!(clientFilter?.trim() || clientNameQuery?.trim());
      if (!hasManualClientFilter) {
        setSelectedClientId('');
        try { localStorage.setItem(EO_K_CLIENTID, ''); } catch {}
      }
    }
  }, [IS_CLIENT, clientFilter, clientNameQuery]);

  const fetchOrders = async (
    offset = 0,
    customLimit = limit,
    clientid = selectedClientId,
    clientname = clientNameQuery
  ) => {
    const thisSeq = ++eordersFetchSeq.current;

    if (eordersFetchAbort.current) {
      try { eordersFetchAbort.current.abort(); } catch {}
    }
    const controller = new AbortController();
    eordersFetchAbort.current = controller;

    setLoadingList(true);
    try {
      const params = new URLSearchParams({ limit: customLimit, offset, sortField, sortOrder });
      if (q) params.set('q', q);
      if (fromDate) params.set('from', fromDate);
      if (toDate) params.set('to', toDate);
      
      // For client users, allow selecting among allowed clients via dropdown; for internal, only when filter applied
      const hasManualClientFilter = !!(clientFilter?.trim() || clientNameQuery?.trim() || clientid);
      if (IS_CLIENT) {
        if (clientid) params.set('clientid', String(clientid));
      } else if (hasManualClientFilter && clientid) {
        params.set('clientid', String(clientid));
      } else if (clientname) {
        params.set('clientname', clientname);
      }
      
      if (serviceTypeFilter) params.set('servicetype', serviceTypeFilter);
      if (storeStatusFilter) params.set('orderstatus', storeStatusFilter);
      if (billedFilter === 'unbilled') params.set('hasinvoice', 'unbilled');
      else if (billedFilter === 'billed') params.set('hasinvoice', 'billed');

      // NEW: send multiple our_status ids; if exactly one, also send legacy param
      if (ourStatusIds.length > 0) {
        params.set('our_order_status_ids', ourStatusIds.join(','));
        if (ourStatusIds.length === 1) params.set('our_order_status', ourStatusIds[0]);
      }

      params.set('deleted', showDeleted ? '1' : '0');

      const json = await fetchJSON(`${API}/api/eorders?${params.toString()}`, { signal: controller.signal });
      if (thisSeq !== eordersFetchSeq.current) return; // ignore stale
      setOrders(json.data || json || []);
      const total = Number(json?.meta?.total || (Array.isArray(json?.data) ? json.data.length : Array.isArray(json) ? json.length : 0));
      const safeLimit = Number.isFinite(+customLimit) && +customLimit > 0 ? +customLimit : DEFAULT_LIMIT;
      const requestedPage = Math.floor((offset || 0) / safeLimit) + 1;
      const computedLastPage = Math.max(1, Math.ceil(total / safeLimit));
      if (requestedPage > computedLastPage) {
        const newOffset = Math.max(0, (computedLastPage - 1) * safeLimit);
        setMeta({ total, limit: safeLimit, offset: newOffset });
        // auto-refetch once; don't loop by letting next call pass same client filters
        fetchOrders(newOffset, safeLimit);
        return;
      }
      setMeta(json.meta || { total, limit: safeLimit, offset });

      // Preserve selection if the selected row still exists in the refreshed list
      try {
        const prevId = selectedEOrderId;
        const arr = Array.isArray(json?.data) ? json.data : (Array.isArray(json) ? json : []);
        if (prevId) {
          const row = arr.find(o => Number(o?.id) === Number(prevId));
          if (!row) {
            setSelectedEOrderId(null);
            setSelectedEOrderMeta({ clientname: '', ordernumber: '' });
          } else {
            // Refresh visible meta for the selected row
            setSelectedEOrderMeta({ clientname: row.clientname || '', ordernumber: row.ordernumber || '' });
          }
        }
      } catch {}
      setSelected(null);
      setEditing(false);
      setCreating(false);
      setSelectedIds(new Set());
    } catch (e) {
      if (e?.name === 'AbortError') return;
      if (thisSeq !== eordersFetchSeq.current) return;
      setOrders([]);
      setMeta({ total: 0, limit: customLimit, offset });
    } finally {
      if (thisSeq === eordersFetchSeq.current) setLoadingList(false);
    }
  };

  

  const syncOrdersNow = async () => {
    setSyncing(true);
    setSyncOpen(false);
    setSyncingNowName(''); // reset any prior name

    // start polling /sync/status and /shopify/progress every 500ms to show progress
    clearInterval(syncStatusTimerRef.current);
    syncStatusTimerRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${API}/api/eorders/sync/status`);
        if (!res.ok) return;
        const st = await res.json();
        let label = st?.running && st?.current?.name ? st.current.name : '';
        // Also fetch Shopify progress (granular page-level details)
        try {
          const pr = await fetch(`${API}/api/shopify/progress`).then(r => r.ok ? r.json() : null).catch(()=>null);
          setShopifyProgress(pr);
          if (pr?.running && pr?.current) {
            const cur = pr.current || {};
            const parts = [];
            if (label) parts.push(label);
            if (cur.name && (!label || cur.name !== label)) parts.push(cur.name);
            if (cur.phase === 'reconcile') {
              const a = Number(cur.reconProcessed || 0), b = Number(cur.reconTotal || 0);
              parts.push(`recon ${a}/${b}`);
            } else if (cur.phase === 'pull') {
              const p = Number(cur.page || 0);
              const P = cur.pages != null ? String(cur.pages) : '?';
              const i = Number(cur.pageProcessed || 0), I = Number(cur.pageTotal || 0);
              parts.push(`page ${p}/${P}`);
              if (I > 0) parts.push(`${i}/${I}`);
              if (cur.pulled != null) parts.push(`pulled ${cur.pulled}`);
            } else if (cur.phase === 'drafts') {
              const p = Number(cur.page || 0);
              const P = cur.pages != null ? String(cur.pages) : '?';
              const i = Number(cur.pageProcessed || 0), I = Number(cur.pageTotal || 0);
              parts.push(`drafts ${p}/${P}`);
              if (I > 0) parts.push(`${i}/${I}`);
            }
            label = parts.join(' — ');
          }
        } catch {}
        setSyncingNowName(label);
      } catch { /* ignore polling errors */ }
    }, 500);

    try {
      const res = await fetch(`${API}/api/eorders/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}) // optional: { clientid, integrationId, since, maxPages }
      });
      const j = await res.json().catch(() => ({}));
      setSyncData(j);
      setSyncOpen(true);
    } catch (e) {
      setSyncData({ success: false, results: [{ error: e.message }] });
      setSyncOpen(true);
    } finally {
      setSyncing(false);
      clearInterval(syncStatusTimerRef.current);
      syncStatusTimerRef.current = null;
      setSyncingNowName('');

      // 🔄 Refresh the E-Orders list in this view
      fetchOrders(meta.offset, limit, selectedClientId, clientNameQuery);

      // 🔔 Also notify any other listeners (e.g., dashboards) to refresh
      window.dispatchEvent(new CustomEvent('eorders:refresh'));
    }
  };

  // Print Packing Slip + Labels (for quick picking)
  const printPackingSlipAndLabels = () => {
      if (!selectedIds.size) {
        alert('Select at least one e-order.');
        return;
      }
      const ids = Array.from(selectedIds);
      ids.forEach((id) => {
        // Packing slip (includes label metadata under each package)
        window.open(`${API}/api/eorders/${id}/packing-slip.pdf?include=labels`, '_blank', 'noopener,noreferrer');
        // Labels bundle (existing route)
        window.open(`${API}/api/eorders/${id}/shipping-labels.pdf`, '_blank', 'noopener,noreferrer');
      });
    };

    // ---- Print helpers ----
  const blockedPopupsRef = useRef([]);
  const openPop = (url, label = 'Link') => {
    // Open a blank window synchronously to avoid false null when using noopener
    const w = window.open('', '_blank');
    if (!w) {
      // Popup blocked — remember for fallback UI
      blockedPopupsRef.current.push({ url, label });
      return false;
    }
    // Security: prevent reverse‑tabnabbing without using features that can return null
    try { w.opener = null; } catch {}
    try { w.location = url; } catch { try { w.document.location.href = url; } catch {} }
    return true;
  };

  // If printing a Pick List, optionally bump status from NEW -> PICKING
  // Only updates orders currently in NEW; leaves other statuses unchanged.
  const bumpStatusToPickingIfNew = async (idsRaw = []) => {
    try {
      const ids = (idsRaw || []).map(Number).filter(n => Number.isFinite(n) && n > 0);
      if (!ids.length) return;
      const list = Array.isArray(statusOptions) && statusOptions.length ? statusOptions
                  : (Array.isArray(ourStatuses) ? ourStatuses : []);
      const PICKING_ID = getStatusIdByName('PICKING', list);
      const NEW_ID     = getStatusIdByName('NEW', list);
      if (!PICKING_ID) return; // cannot resolve target id

      const idsToUpdate = [];
      ids.forEach(id => {
        const row = orders.find(o => Number(o.id) === Number(id));
        const name = String(row?.ourstatusname || '').trim().toUpperCase();
        const curId = Number(row?.our_order_status || 0);
        const isNew = name === 'NEW' || (NEW_ID && curId === NEW_ID);
        if (isNew) idsToUpdate.push(id);
      });
      if (!idsToUpdate.length) return;

      const res = await fetch(`${API}/api/eorders/bulk/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: idsToUpdate, our_order_status: PICKING_ID })
      });
      // Update UI optimistically regardless of response; best-effort sync
      setOrders(prev => prev.map(o => (
        idsToUpdate.includes(o.id)
          ? { ...o, our_order_status: PICKING_ID, ourstatusname: 'PICKING' }
          : o
      )));
      try { if (!res.ok) { /* ignore error for printing path */ } } catch {}
    } catch {}
  };

  const printPickListOnly = () => {
    if (!requireSelection()) return;
    blockedPopupsRef.current = [];
    const ids = Array.from(selectedIds).map(Number).filter(n => Number.isFinite(n) && n > 0);
    if (ids.length === 1) {
      openPop(`${API}/api/eorders/${ids[0]}/picklist.pdf`, 'Pick List');
    } else {
      const qs = ids.join(','); // raw comma-list
      // Also include repeated ids[]=... form for maximum server compatibility
      const qsRep = ids.map(id => `ids[]=${id}`).join('&');
      // Prefer comma list; include repeated form as backup parameter
      openPop(`${API}/api/eorders/picklist-multiple.pdf?ids=${qs}&${qsRep}`, 'Pick List');
    }
    // After initiating print, bump status NEW -> PICKING for those ids
    bumpStatusToPickingIfNew(ids);
    if (blockedPopupsRef.current.length) {
      const msg = 'Your browser blocked one or more popups.\nPlease click the links below to open them:\n\n' +
        blockedPopupsRef.current.map(b => `${b.label}: ${b.url}`).join('\n');
      alert(msg);
    }
  };

  // ===== Auto-refresh E-Orders list based on user setting =====
  const listTimerRef = useRef(null);
  const configureListTimer = (sec) => {
    try { if (listTimerRef.current) clearInterval(listTimerRef.current); } catch {}
    listTimerRef.current = null;
    const seconds = Math.max(0, Number(sec || 0));
    if (seconds <= 0) return; // disabled
    listTimerRef.current = setInterval(() => {
      // Avoid refreshing while editing/creating to prevent data loss
      if (loadingList || editing || creating) return;
      // Re-use current filters/pagination
      fetchOrders(meta.offset, limit, selectedClientId, clientNameQuery);
    }, Math.max(5000, seconds * 1000)); // safety minimum 5s
  };

  useEffect(() => {
    // Ensure the auto-refresh timer always uses the latest filters/search/sort
    // by reconfiguring it whenever those change.
    configureListTimer(getRefreshSeconds());
    const onChanged = (e) => configureListTimer(e?.detail?.seconds ?? getRefreshSeconds());
    window.addEventListener('eorders:refreshIntervalChanged', onChanged);
    return () => {
      window.removeEventListener('eorders:refreshIntervalChanged', onChanged);
      try { if (listTimerRef.current) clearInterval(listTimerRef.current); } catch {}
    };
  }, [
    q,
    clientFilter,
    selectedClientId,
    clientNameQuery,
    fromDate,
    toDate,
    datePreset,
    serviceTypeFilter,
    storeStatusFilter,
    billedFilter,
    ourStatusIds,
    showDeleted,
    sortField,
    sortOrder,
    limit,
    loadingList,
    editing,
    creating,
  ]);

  const printPickAndShip = () => {
    // Renamed behavior: only print shipping labels
    if (!requireSelection()) return;
    blockedPopupsRef.current = [];
    Array.from(selectedIds).forEach((id) => {
      openPop(`${API}/api/eorders/${id}/shipping-labels.pdf`, 'Labels');
    });
    if (blockedPopupsRef.current.length) {
      const msg = 'Your browser blocked one or more popups.\nPlease click the links below to open them:\n\n' +
        blockedPopupsRef.current.map(b => `${b.label}: ${b.url}`).join('\n');
      alert(msg);
    }
  };

  const printPackSlipsOnly = () => {
    if (!requireSelection()) return;
    blockedPopupsRef.current = [];
    Array.from(selectedIds).forEach((id) => {
      openPop(`${API}/api/eorders/${id}/packing-slip.pdf`, 'Packing Slip');
    });
    if (blockedPopupsRef.current.length) {
      const msg = 'Your browser blocked one or more popups.\nPlease click the links below to open them:\n\n' +
        blockedPopupsRef.current.map(b => `${b.label}: ${b.url}`).join('\n');
      alert(msg);
    }
  };

  const printAll = () => {
    if (!requireSelection()) return;
    blockedPopupsRef.current = [];
    const orderMap = new Map(orders.map((o) => [Number(o.id), o]));
    const skipped = [];
    const ids = Array.from(selectedIds)
      .map((rawId) => Number(rawId))
      .filter((id) => Number.isFinite(id) && id > 0);
    ids.forEach((id) => {
      openPop(`${API}/api/eorders/${id}/picklist.pdf`, 'Pick List');
      openPop(`${API}/api/eorders/${id}/packing-slip.pdf`, 'Pack List');
      const order = orderMap.get(id);
      if (!order) {
        openPop(`${API}/api/eorders/${id}/shipping-labels.pdf`, 'Labels');
        return;
      }
      const rawLabelCount = order?.labelcount ?? order?.label_count ?? order?.labelCount ?? null;
      const labelCount = rawLabelCount == null ? null : Number(rawLabelCount);
      if (labelCount == null || !Number.isFinite(labelCount)) {
        openPop(`${API}/api/eorders/${id}/shipping-labels.pdf`, 'Labels');
        return;
      }
      if (labelCount <= 0) {
        const display = order.ordernumber || `#${id}`;
        skipped.push(display);
        return;
      }
      openPop(`${API}/api/eorders/${id}/shipping-labels.pdf`, 'Labels');
    });
    if (ids.length) {
      bumpStatusToPickingIfNew(ids);
    }
    if (skipped.length) {
      const preview = skipped.slice(0, 3).join(', ');
      const more = skipped.length > 3 ? ` (+${skipped.length - 3} more)` : '';
      showToast({
        id: `eorders.printAll.nolabels.${Date.now()}`,
        message: `Skipped labels for ${skipped.length} order${skipped.length === 1 ? '' : 's'}${preview ? ` (${preview}${more})` : ''} — none exist yet.`,
        duration: 3600,
        align: 'center',
        slideFrom: 'top',
        pill: true
      });
    }
    if (blockedPopupsRef.current.length) {
      const msg = 'Your browser blocked one or more popups.\nPlease click the links below to open them:\n\n' +
        blockedPopupsRef.current.map(b => `${b.label}: ${b.url}`).join('\n');
      alert(msg);
    }
  };

  // Manual refresh for carrier status on a specific order
  const refreshCarrierStatus = async (order) => {
    if (!order?.id) return;
    try {
      window.dispatchEvent(new CustomEvent('topbar:setStatus', { detail: { status: `Syncing tracking for #${order.ordernumber || order.id}…` } }));
      const res = await fetch(`${API}/api/eorders/${order.id}/refresh-carrier-status?debug=1`, { method: 'POST' });
      const j = await res.json().catch(()=>({}));
      if (!res.ok || j?.error) {
        openNotify('error', 'Tracking Refresh Failed', j?.error || `HTTP ${res.status}`);
      } else if (j?.changed) {
        showToast({ id: 'eorders.tracking.updated', message: 'Order status updated from carrier.', duration: 2000, align: 'center', slideFrom: 'top', pill: true });
        fetchOrders(meta.offset, limit);
      } else {
        openNotify('warn', 'No Change', 'Carrier did not indicate in‑transit yet.');
      }
    } catch (e) {
      openNotify('error', 'Tracking Refresh Failed', e.message);
    } finally {
      window.dispatchEvent(new CustomEvent('topbar:clearStatus'));
    }
  };

  // USE EFFECT ///////////////////////////////////////////////////////////////////////////////////////
  // -------- persist filters ----------
  useEffect(() => { try { localStorage.setItem(EO_K_Q, q); } catch {} }, [q]);
  // De-dupe guard for fetch triggers (helps with StrictMode double-invoke)
  const lastQueryKeyRef = useRef('');
  useEffect(() => { try { localStorage.setItem(EO_K_FROM, fromDate); } catch {} }, [fromDate]);
  useEffect(() => { try { localStorage.setItem(EO_K_TO, toDate); } catch {} }, [toDate]);
  useEffect(() => { try { localStorage.setItem(EO_K_PRESET, datePreset); } catch {} }, [datePreset]);
  useEffect(() => { try { localStorage.setItem(EO_K_CLIENTNAME, clientFilter); } catch {} }, [clientFilter]);
  useEffect(() => { try { localStorage.setItem(EO_K_CLIENTID, String(selectedClientId || '')); } catch {} }, [selectedClientId]);
  useEffect(() => { try { localStorage.setItem(EO_K_SERVICETYPE, serviceTypeFilter); } catch {} }, [serviceTypeFilter]);
  useEffect(() => { try { localStorage.setItem(EO_K_STORESTATUS, storeStatusFilter); } catch {} }, [storeStatusFilter]);

  // Persist our status ids (multi)
  useEffect(() => {
    try { localStorage.setItem(EO_K_OURSTATUS_IDS, ourStatusIds.join(',')); } catch {}
  }, [ourStatusIds]);

  useEffect(() => { try { localStorage.setItem(EO_K_SHOWDELETED, showDeleted ? '1' : '0'); } catch {} }, [showDeleted]);
  useEffect(() => { try { localStorage.setItem(EO_K_LIMIT, String(limit)); } catch {} }, [limit]);
  useEffect(() => {
    try {
      localStorage.setItem(EO_K_SORT, JSON.stringify({ sortField, sortOrder }));
      localStorage.setItem(LS_SORT_KEY, JSON.stringify({ sortField, sortOrder }));
    } catch {}
  }, [sortField, sortOrder]);

  // -------- effects ----------
  // Initial: just apply preset ranges; unified effect below will fetch
  useEffect(() => { 
    if (datePreset) applyDatePreset(datePreset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { fetchClientOptions(); fetchMetaFilters(); fetchStatusOptions(); }, []);
  // Ensure editor has accurate numeric our_order_status even if list didn’t include it
  useEffect(() => {
    (async () => {
      if (!editing || !selected?.id) return;
      try {
        const row = await fetchJSON(`${API}/api/eorders/${selected.id}`);
        const n = Number(row?.our_order_status || 0);
        if (!n) return;
        setSelected(prev => {
          // Avoid overwriting if user already picked a value
          const cur = Number(prev?.our_order_status || 0);
          if (cur) return prev;
          return { ...prev, our_order_status: n, ourstatusname: row?.ourstatusname || prev?.ourstatusname };
        });
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, selected?.id]);

  useEffect(() => {
    if (!clientFilter) { setFilteredClientOptions(clientOptions); return; }
    setFilteredClientOptions(clientOptions.filter(opt =>
      (opt.name || '').toLowerCase().includes(clientFilter.toLowerCase())
    ));
  }, [clientFilter, clientOptions]);

// Unified search/sort/filter/limit effect (debounced + de-duplicated)
useEffect(() => {
  const signature = JSON.stringify({
    q: (q || '').trim(),
    clientFilter: (clientFilter || '').trim(),
    selectedClientId: selectedClientId || '',
    clientNameQuery: (clientNameQuery || '').trim(),
    fromDate: fromDate || '',
    toDate: toDate || '',
    datePreset: datePreset || '',
    serviceTypeFilter: serviceTypeFilter || '',
    storeStatusFilter: storeStatusFilter || '',
    billedFilter: billedFilter || '',
    ourStatusIds: (ourStatusIds || []).slice().sort(),
    showDeleted: !!showDeleted,
    sortField,
    sortOrder,
    limit,
  });
  const t = setTimeout(() => {
    if (lastQueryKeyRef.current === signature) return;
    lastQueryKeyRef.current = signature;
    setOrders([]);
    setMeta({ total: 0, limit, offset: 0 });
    fetchOrders(0, limit, selectedClientId, clientNameQuery);
  }, SEARCH_DEBOUNCE_MS);
  return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [q, clientFilter, selectedClientId, clientNameQuery, fromDate, toDate, datePreset, serviceTypeFilter, storeStatusFilter, billedFilter, ourStatusIds, showDeleted, sortField, sortOrder, limit]);

// When orders list refreshes and we have a pending scroll id, reselect and scroll into view
useEffect(() => {
  const id = pendingScrollIdRef.current;
  if (!id) return;
  const exists = Array.isArray(orders) && orders.some(o => o.id === id);
  if (!exists) return;
  // Select row and scroll smoothly
  setSelectedEOrderId(id);
  // Give DOM a tick to render selection, then scroll row into view
  const table = eordTableRef.current;
  const scroll = () => {
    const row = table?.querySelector(`tbody tr[data-eorderid="${id}"]`);
    if (row && typeof row.scrollIntoView === 'function') {
      try { row.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' }); } catch {}
    }
    pendingScrollIdRef.current = null;
  };
  requestAnimationFrame(() => requestAnimationFrame(scroll));
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [orders]);

// show shipments
useEffect(() => {
  // when user clicks a different e-order, default to showing items in bottom pane
  if (selectedEOrderId) {
    setEoBottomPaneMode(prev => prev || 'items');
    setEoSplitHeights(prev => (Array.isArray(prev) && prev[1] > 0 ? prev : [65, 35]));
  }
}, [selectedEOrderId]);

// Ensure selected row stays visible when opening Items pane
useEffect(() => {
  if (!selectedEOrderId) return;
  const table = eordTableRef.current;
  const container = eordListColRef.current || table?.parentElement;
  if (!table || !container) return;
  const scrollIntoView = () => {
    const row = table.querySelector(`tbody tr[data-eorderid="${selectedEOrderId}"]`);
    if (row && typeof row.scrollIntoView === 'function') {
      try { row.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' }); } catch {}
    }
  };
  // Let the split resize settle before scrolling
  requestAnimationFrame(() => requestAnimationFrame(scrollIntoView));
}, [selectedEOrderId, eoBottomPaneMode]);

// Load bottom-pane data on selection/mode change
useEffect(() => {
  if (!selectedEOrderId) { setEoHistory([]); setEoAttachments([]); return; }
  if (eoBottomPaneMode === 'history') fetchEOrderHistory(selectedEOrderId);
  if (eoBottomPaneMode === 'attachments') fetchEOrderAttachments(selectedEOrderId);
}, [selectedEOrderId, eoBottomPaneMode]);

// Refresh E-Orders list when label modal signals completion
useEffect(() => {
  const handler = async () => {
    try {
      const list = ordersRef.current || [];
      if (!Array.isArray(list) || !list.length) return;
      const ids = list.map(o => Number(o.id)).filter(n => Number.isFinite(n) && n > 0);
      const res = await fetch(`${API}/api/eorders/statuses?ids=${ids.join(',')}`);
      if (!res.ok) return;
      const rows = await res.json().catch(() => []);
      if (!Array.isArray(rows) || !rows.length) return;
      const byId = new Map(rows.map(r => [Number(r.id), r]));
      setOrders(prev => prev.map(o => {
        const r = byId.get(Number(o.id));
        if (!r) return o;
        const next = { ...o };
        if (typeof r.our_order_status === 'number' && r.our_order_status !== o.our_order_status) next.our_order_status = r.our_order_status;
        if (r.ourstatusname && r.ourstatusname !== o.ourstatusname) next.ourstatusname = r.ourstatusname;
        return next;
      }));
    } catch { /* ignore */ }
  };
  window.addEventListener('eorders:refresh', handler);
  return () => window.removeEventListener('eorders:refresh', handler);
}, []);
    
// --- Topbar title (set after render; clear on unmount) ---
useEffect(() => {
  const subtitle = meta?.total > 0 ? `(${Number(meta.total).toLocaleString()} found)` : '';
  window.dispatchEvent(new CustomEvent('topbar:setTitle', {
    detail: { title: 'E-Orders', subtitle }
  }));
  return () => {
    window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
  };
}, [meta?.total]);

useEffect(() => {
    if (IS_CLIENT && CLIENT_ID) {
      setSelectedClientId(String(CLIENT_ID));
      setClientFilter('');
      setClientNameQuery('');
      try { localStorage.setItem(EO_K_CLIENTID, String(CLIENT_ID)); } catch {}
    }
  }, [IS_CLIENT, CLIENT_ID]);

// -------- paging/sort ----------
const page = useMemo(() =>
  Math.floor((meta.offset || 0) / (limit || DEFAULT_LIMIT)) + 1, [meta.offset, limit]);
const lastPage = useMemo(() =>
  Math.max(1, Math.ceil((meta.total || 0) / (limit || DEFAULT_LIMIT))), [meta.total, limit]);

const gotoPage = (p) => {
  const lp = Math.max(1, Math.ceil((meta.total || 0) / (limit || DEFAULT_LIMIT)));
  const clamped = Math.min(Math.max(1, p), lp);
  const nextOffset = Math.max(0, (clamped - 1) * limit);
  setOrders([]);
  setMeta({ total: meta.total || 0, limit, offset: nextOffset });
  fetchOrders(nextOffset, limit);
};

  const handleSort = (field, e) => {
    // Ignore sort when clicking/just after using the resizer
    if (e?.target && typeof e.target.closest === 'function' && e.target.closest('.col-resizer')) return;
    if (eordResizedRecently.current) return;
    let nextOrder = 'ASC';
    if (sortField === field) nextOrder = sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortField(field);
    setSortOrder(nextOrder);
    localStorage.setItem(LS_SORT_KEY, JSON.stringify({ sortField: field, sortOrder: nextOrder }));
    localStorage.setItem(EO_K_SORT, JSON.stringify({ sortField: field, sortOrder: nextOrder }));
    setOrders([]);
    setMeta({ total: 0, limit, offset: 0 });
    fetchOrders(0, limit);
  };

  // -------- date preset ----------
  const applyDatePreset = (preset) => {
    const today = new Date();
    let from = '', to = '';

    switch (preset) {
      case 'all': { from = ''; to = ''; break; }
      case 'today': { const t = fmtYMD(today); from = t; to = t; break; }
      case 'yesterday': { const y = new Date(today); y.setDate(y.getDate() - 1); const t = fmtYMD(y); from = t; to = t; break; }
      case 'this_week': { const s = startOfWeekMon(today); from = fmtYMD(s); to = fmtYMD(today); break; }
      case 'last_week': {
        const s0 = startOfWeekMon(today);
        const s  = new Date(s0); s.setDate(s0.getDate() - 7);
        const e  = new Date(s);  e.setDate(s.getDate() + 6);
        from = fmtYMD(s); to = fmtYMD(e); break;
      }
      case 'this_month': { const s = startOfMonth(today); const e = endOfMonth(today); from = fmtYMD(s); to = fmtYMD(e); break; }
      case 'last_month': {
        const s = startOfMonth(new Date(today.getFullYear(), today.getMonth() - 1, 1));
        const e = endOfMonth(s); from = fmtYMD(s); to = fmtYMD(e); break;
      }
      case 'this_year': { const s = startOfYear(today.getFullYear()); from = fmtYMD(s); to = fmtYMD(today); break; }
      case 'last_year': { const y = today.getFullYear() - 1; from = fmtYMD(startOfYear(y)); to = fmtYMD(endOfYear(y)); break; }
      default: { localStorage.setItem(LS_DATE_PRESET, ''); setDatePreset(''); return; }
    }

    setFromDate(from);
    setToDate(to);
    setDatePreset(preset);
    localStorage.setItem(LS_DATE_PRESET, preset);
    localStorage.setItem(EO_K_PRESET, preset);

    // Let the unified effect pick up these state changes and fetch
    setOrders([]);
    setMeta({ total: 0, limit, offset: 0 });
  };

  // -------- actions ----------
  const onEdit = (order) => {
    const base = { ...emptyEOrder, ...order };
    // If the list row lacks numeric status, try map from name immediately
    if (!Number(base.our_order_status) && order?.ourstatusname && Array.isArray(statusOptions) && statusOptions.length) {
      const id = getStatusIdByName(order.ourstatusname, statusOptions);
      if (id) base.our_order_status = id;
    }
    setSelected(base);
    setEditing(true);
    setCreating(false);
    setDetail(null);
  };
  const onNew = () => setNewOpen(true);

  // open inventory picker for a given row
  const openPickerFor = (order) => {
    setPickerFor({
      id: order.id,
      clientid: order.clientid || '',
      clientname: order.clientname || '',
      ordernumber: order.ordernumber || order.id,
      warehouseid: order.warehouseid || ''
    });
    setPickerOpen(true);
  };

  // ====== E-Order History & Attachments ======
  const fetchEOrderHistory = async (eorderId) => {
    if (!eorderId || IS_CLIENT) { setEoHistory([]); return; }
    setEoHistoryLoading(true);
    try {
      const data = await fetchJSON(`${API}/api/eorders/${eorderId}/history`);
      setEoHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      setEoHistory([]);
    } finally {
      setEoHistoryLoading(false);
    }
  };
  const fetchEOrderAttachments = async (eorderId) => {
    if (!eorderId) { setEoAttachments([]); return; }
    setEoAttachmentsLoading(true);
    try {
      const data = await fetchJSON(`${API}/api/eorders/${eorderId}/attachments`);
      setEoAttachments(Array.isArray(data) ? data : []);
    } catch (e) {
      setEoAttachments([]);
    } finally {
      setEoAttachmentsLoading(false);
    }
  };

  const uploadEOrderFiles = async (files) => {
    if (!selectedEOrderId || !files?.length) return;
    try {
      const formData = new FormData();
      files.forEach((f) => formData.append('files', f));
      if (eoAttachDesc) formData.append('description', eoAttachDesc);

      const res = await fetch(`${API}/api/eorders/${selectedEOrderId}/attachments`, { method: 'POST', body: formData });
      const json = await res.json().catch(()=>({}));
      if (!res.ok || json?.success === false) {
        openNotify('error', 'Error', json?.error || 'Upload failed');
        return;
      }
      await fetchEOrderAttachments(selectedEOrderId);
      setEoPendingFiles([]);
      setEoAttachDesc('');
    } catch (err) {
      openNotify('error', 'Error', 'Upload failed: ' + err.message);
    }
  };

  const deleteEOrderAttachment = async (attId) => {
    if (!selectedEOrderId || !attId) return;
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await fetchJSON(`${API}/api/eorders/${selectedEOrderId}/attachments/${attId}`, { method: 'DELETE' });
      await fetchEOrderAttachments(selectedEOrderId);
    } catch (e) {
      openNotify('error', 'Error', 'Delete failed: ' + e.message);
    }
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      const payload = { ...selected };

      // never send server-managed/join fields
      delete payload.datecreated;
      delete payload.updatedat;
      delete payload.clientname;     // <-- drop joined field
      delete payload.ourstatusname;  // <-- drop joined field

      if (payload.orderdate) {
        // If user provided a date only, append current time so we keep a meaningful timestamp
        if (/^\d{4}-\d{2}-\d{2}$/.test(String(payload.orderdate))) {
          const now = new Date();
          const pad = (n) => String(n).padStart(2, '0');
          const hh = pad(now.getHours());
          const mm = pad(now.getMinutes());
          const ss = pad(now.getSeconds());
          payload.orderdate = `${payload.orderdate} ${hh}:${mm}:${ss}`;
        } else {
          const norm = toSQLDateTime(payload.orderdate);
          if (norm) payload.orderdate = norm; else delete payload.orderdate;
        }
      }
      if (payload.shipbydate) {
        const norm = toSQLDateStart(payload.shipbydate);
        if (norm) payload.shipbydate = norm; else delete payload.shipbydate;
      }

      if (IS_CLIENT && CLIENT_ID) payload.clientid = CLIENT_ID;

      const method = !creating ? 'PUT' : 'POST';
      const url = !creating ? `${API}/api/eorders/${selected.id}` : `${API}/api/eorders`;
      await fetchJSON(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      // Persist tracking numbers (if any) along with main save. This updates
      // the trackingnumber field(s) on existing labels/shipments.
      try { await saveMetaTracking(); } catch {}

      // After saving, refresh list and scroll to the saved order row
      pendingScrollIdRef.current = selected.id;
      fetchOrders(meta.offset, limit);
      setSelected(null);
      setEditing(false);
      setCreating(false);
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const restoreOrder = async (id) => {
    try { await fetchJSON(`${API}/api/eorders/${id}/restore`, { method: 'PUT' }); await fetchOrders(meta.offset, limit); }
    catch (e) { alert(e.message); }
  };

  const deleteOrder = async (id) => {
    try {
      await fetchJSON(`${API}/api/eorders/${id}`, { method: 'DELETE', headers: { 'Content-Type': 'application/json' } });
      fetchOrders(meta.offset, limit);
      if (selected?.id === id) setSelected(null);
      if (selectedEOrderId === id) { setSelectedEOrderId(null); setSelectedEOrderMeta({ clientname: '', ordernumber: '' }); }
    } catch (e) { alert(e.message); }
  };

  const openSplitModal = () => {
    if (!canSplitOrder) return;
    setSplitModalOpen(true);
  };

  const closeSplitModal = () => {
    if (splitBusy) return;
    setSplitModalOpen(false);
    setSplitLines([]);
    setSplitSelectedLines(new Set());
    setSplitQuantities({});
    setSplitLinesLoading(false);
    setSplitError('');
  };

  const toggleSplitLine = (line, disabled = false) => {
    if (!line || disabled || splitBusy) return;
    setSplitError('');
    const next = new Set(splitSelectedLines);
    if (next.has(line.id)) {
      next.delete(line.id);
    } else {
      const max = line.availableQty ?? getLineAvailableQty(line);
      if (max <= 0) {
        setSplitError('No available quantity to move for this line.');
        return;
      }
      next.add(line.id);
      setSplitQuantities(prev => {
        const updated = { ...prev };
        const current = Number(updated[line.id] || 0);
        updated[line.id] = current > 0 ? Math.min(current, max) : max;
        return updated;
      });
    }
    setSplitSelectedLines(next);
  };

  const selectAllSplitLines = () => {
    if (splitBusy) return;
    const nextSet = new Set();
    const nextQty = { ...splitQuantities };
    splitLines.forEach((line) => {
      const shipped = Number(line.qtyShipped || 0) > 0;
      const available = line.availableQty ?? getLineAvailableQty(line);
      if (!shipped && available > 0) {
        nextSet.add(line.id);
        nextQty[line.id] = available;
      } else {
        nextQty[line.id] = 0;
      }
    });
    if (!nextSet.size) {
      setSplitError('No line items have unallocated quantity available to move.');
      return;
    }
    setSplitError('');
    setSplitSelectedLines(nextSet);
    setSplitQuantities(nextQty);
  };

  const clearSplitSelection = () => {
    if (splitBusy) return;
    setSplitSelectedLines(new Set());
    setSplitQuantities(prev => {
      const next = { ...prev };
      splitLines.forEach(line => { next[line.id] = 0; });
      return next;
    });
  };

  const updateSplitQuantity = (line, rawValue) => {
    if (!line || splitBusy) return;
    const max = line.availableQty ?? getLineAvailableQty(line);
    let val = Number(rawValue);
    if (!Number.isFinite(val)) val = 0;
    val = Math.max(0, Math.min(max, val));
    setSplitQuantities(prev => ({ ...prev, [line.id]: val }));
    if (val > 0) {
      setSplitSelectedLines(prev => {
        if (prev.has(line.id)) return prev;
        const next = new Set(prev);
        next.add(line.id);
        return next;
      });
    } else {
      setSplitSelectedLines(prev => {
        if (!prev.has(line.id)) return prev;
        const next = new Set(prev);
        next.delete(line.id);
        return next;
      });
    }
    setSplitError('');
  };

  const handleSplitSubmit = async () => {
    if (!selectedOneId) return;
    const lineById = new Map(splitLines.map(line => [line.id, line]));
    const payloadItems = [];
    splitSelectedLines.forEach((lineId) => {
      const line = lineById.get(lineId);
      if (!line) return;
      const available = line.availableQty ?? getLineAvailableQty(line);
      const qty = Math.min(available, Math.max(0, Number(splitQuantities[lineId] || 0)));
      if (qty > 0) {
        payloadItems.push({ lineId, quantity: qty });
      }
    });
    if (!payloadItems.length) {
      setSplitError('Select at least one line item with a quantity to move.');
      return;
    }
    const allLinesMoved =
      payloadItems.length === splitLines.length &&
      payloadItems.every((item) => {
        const line = lineById.get(item.lineId);
        return line && item.quantity >= Number(line.qtyOrdered || 0);
      });
    if (allLinesMoved) {
      setSplitError('At least one line item must remain on the original order.');
      return;
    }
    setSplitBusy(true);
    setSplitError('');
    try {
      const body = { items: payloadItems };
      const result = await fetchJSON(`${API}/api/eorders/${selectedOneId}/split`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const newId = Number(result?.newOrderId || 0);
      const newNumber = result?.newOrderNumber || '';
      setSplitBusy(false);
      closeSplitModal();
      if (Number.isFinite(newId) && newId > 0) {
        pendingScrollIdRef.current = newId;
      }
      await fetchOrders(meta.offset, limit, selectedClientId, clientNameQuery);
      if (Number.isFinite(newId) && newId > 0) {
        setSelectedIds(new Set([newId]));
      }
      if (newNumber) {
        showToast({
          id: `eorders.split.${newNumber}`,
          message: `Created split order #${newNumber}`,
          duration: 2400,
          align: 'center',
          slideFrom: 'top',
          pill: true
        });
      } else {
        showToast({
          id: `eorders.split.${Date.now()}`,
          message: 'Split order created.',
          duration: 2200,
          align: 'center',
          slideFrom: 'top',
          pill: true
        });
      }
    } catch (err) {
      setSplitBusy(false);
      setSplitError(err.message || 'Failed to split order.');
    }
  };

  const duplicateEOrder = async () => {
    if (!canSplitOrder || !selectedOneId) {
      setMoreOpen(false);
      openNotify('warn', 'No Selection', 'Select exactly one e-order.');
      return;
    }
    if (duplicateBusy) return;
    setMoreOpen(false);
    setDuplicateBusy(true);
    try {
      const result = await fetchJSON(`${API}/api/eorders/${selectedOneId}/duplicate`, {
        method: 'POST'
      });
      const newId = Number(result?.newOrderId || 0);
      const newNumber = result?.newOrderNumber || '';
      if (Number.isFinite(newId) && newId > 0) {
        pendingScrollIdRef.current = newId;
      }
      await fetchOrders(meta.offset, limit, selectedClientId, clientNameQuery);
      if (Number.isFinite(newId) && newId > 0) {
        setSelectedIds(new Set([newId]));
      }
      if (newNumber) {
        showToast({
          id: `eorders.duplicate.${newNumber}`,
          message: `Duplicated order #${newNumber}`,
          duration: 2400,
          align: 'center',
          slideFrom: 'top',
          pill: true
        });
      } else {
        showToast({
          id: `eorders.duplicate.${Date.now()}`,
          message: 'Duplicate order created.',
          duration: 2200,
          align: 'center',
          slideFrom: 'top',
          pill: true
        });
      }
    } catch (err) {
      openNotify('error', 'Duplicate Failed', err.message || 'Failed to duplicate e-order.');
    } finally {
      setDuplicateBusy(false);
    }
  };

  // bulk actions
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkStatusValue, setBulkStatusValue] = useState('');
  const toggleSelect = (id) => setSelectedIds(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const toggleSelectAllOnPage = () => { if (allSelected) setSelectedIds(new Set()); else setSelectedIds(new Set(orders.map(o => o.id))); };
  const bulkDelete = async () => {
    if (!selectedIds.size) { openNotify('warn', 'No Selection', 'Select at least one e-order.'); return; }
    openConfirm(`Delete ${selectedIds.size} selected e-order(s)?`, async () => {
    try {
      const res = await fetch(`${API}/api/eorders/bulk`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds) })
      });
      if (!res.ok) throw new Error((await res.json().catch(()=>({}))).error || 'Bulk delete failed');
      await fetchOrders(meta.offset, limit);
    } catch (e) { openNotify('error', 'Bulk Delete Failed', e.message); }
    }, { title: 'Confirm Delete', confirmText: 'Delete', cancelText: 'Cancel' });
  };
  const openBulkStatus = () => { if (!selectedIds.size) { openNotify('warn', 'No Selection', 'Select at least one e-order.'); return; } setBulkStatusOpen(true); };
  const applyBulkStatus = async () => {
    if (!bulkStatusValue) return openNotify('warn', 'Missing Status', 'Pick a status.');
    try {
      const res = await fetch(`${API}/api/eorders/bulk/status`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: Array.from(selectedIds), our_order_status: bulkStatusValue })
      });
      if (!res.ok) throw new Error((await res.json().catch(()=>({}))).error || 'Bulk status update failed');
      const chosen = statusOptions.find(opt => String(opt.id) === String(bulkStatusValue));
      const newName = chosen?.name || '';
      const newId = parseInt(bulkStatusValue, 10) || null;
      setOrders(prev => prev.map(o => (selectedIds.has(o.id) ? { ...o, ourstatusname: newName || o.ourstatusname, our_order_status: newId ?? o.our_order_status } : o)));
      setBulkStatusOpen(false);
      showToast({ id: 'eorders.bulk.status', message: `Updated status for ${selectedIds.size} order${selectedIds.size === 1 ? '' : 's'}.`, duration: 2000, align: 'center', slideFrom: 'top', pill: true });
    } catch (e) { alert(e.message); }
  };

  // export
  const exportCsv = () => {
    const params = new URLSearchParams({ sort: sortField, order: sortOrder });
    if (q) params.set('q', q);
    if (fromDate) params.set('from', fromDate);
    if (toDate) params.set('to', toDate);
    
    const enforcedClientId = IS_CLIENT && CLIENT_ID ? String(CLIENT_ID) : (selectedClientId || '');
    if (enforcedClientId) params.set('clientid', enforcedClientId);
    else if (clientNameQuery) params.set('clientname', clientNameQuery);
    
    if (serviceTypeFilter) params.set('servicetype', serviceTypeFilter);
    if (storeStatusFilter) params.set('orderstatus', storeStatusFilter);
    if (ourStatusIds.length > 0) {
      params.set('our_order_status_ids', ourStatusIds.join(','));
      if (ourStatusIds.length === 1) params.set('our_order_status', ourStatusIds[0]);
    }
    params.set('deleted', showDeleted ? '1' : '0');
    window.open(`${API}/api/eorders/export?${params.toString()}`, '_blank');
  };

  // Download E-Orders CSV template
  const handleDownloadEOrdersTemplate = () => {
    const headers = [
      'ordernumber',
      'sku',
      'qty',
      'clientid',
      'clientname',
      'shippingcompany',
      'shippingfirstname',
      'shippinglastname',
      'shippingaddress1',
      'shippingaddress2',
      'shippingcity',
      'shippingstate',
      'shippingpostal',
      'shippingcountry',
      'shippingphone',
      'shippingemail',
      'shippingmethod',
      'shippingaccount'
    ];
    const sample = [
      'EO-1001',
      'SKU-123',
      '2',
      '',
      'Acme Widgets',
      'Acme HQ',
      'John',
      'Doe',
      '123 Main St',
      'Suite 200',
      'Miami',
      'FL',
      '33101',
      'US',
      '305-555-0101',
      'john.doe@example.com'
    ];
    const csv = `${headers.join(',')}\n${sample.map(v => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n') ? '"' + s.replace(/"/g, '""') + '"' : s;
    }).join(',')}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'eorders_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // --- Import E-Orders: preview modal like Receive/Items ---
  const [showEordImportPreview, setShowEordImportPreview] = useState(false);
  const [eordPreviewData, setEordPreviewData] = useState([]);
  const [eordPreviewErrors, setEordPreviewErrors] = useState([]);
  const [eordSelectedFile, setEordSelectedFile] = useState(null);
  const eordValidCount = useMemo(() => eordPreviewData.filter(r => r?.valid).length, [eordPreviewData]);
  const eordInvalidCount = useMemo(() => eordPreviewData.filter(r => r && r.valid === false).length + (eordPreviewErrors?.length || 0), [eordPreviewData, eordPreviewErrors]);

  // Import E-Orders CSV preview/import handler
  const handleEOrdersImportPreview = async (file) => {
    if (!file) return;
    setEordImportLoading(true);
    try {
      setEordSelectedFile(file);
      const fd = new FormData();
      fd.append('file', file);

      // Preview
      const prevRes = await fetch(`${API}/api/eorders/import/preview`, { method: 'POST', body: fd });
      const prev = await prevRes.json().catch(() => ({}));
      if (!prevRes.ok) throw new Error(prev?.error || 'Preview failed');

      setEordPreviewData(Array.isArray(prev?.data) ? prev.data : []);
      setEordPreviewErrors(Array.isArray(prev?.errors) ? prev.errors : []);
      setShowEordImportPreview(true);
    } catch (e) {
      openNotify('error', 'Import Failed', e.message || 'Import failed');
    } finally {
      setEordImportLoading(false);
    }
  };

  const handleEOrdersConfirmImport = async () => {
    if (!eordSelectedFile) return;
    try {
      setEordImportLoading(true);
      const fd = new FormData();
      fd.append('file', eordSelectedFile);
      const impRes = await fetch(`${API}/api/eorders/import`, { method: 'POST', body: fd });
      const imp = await impRes.json().catch(() => ({}));
      if (!impRes.ok) throw new Error(imp?.error || 'Import failed');
      const inserted = imp?.inserted ?? imp?.created ?? imp?.added ?? imp?.count ?? 0;
      openNotify('success', 'Import Complete', `Created/updated ${inserted} row(s).`);
      setShowEordImportPreview(false);
      setEordPreviewData([]);
      setEordPreviewErrors([]);
      setEordSelectedFile(null);
      if (eorderImportInputRef.current) eorderImportInputRef.current.value = '';
      fetchOrders(0, limit);
    } catch (e) {
      openNotify('error', 'Import Failed', e.message || 'Import failed');
    } finally {
      setEordImportLoading(false);
    }
  };

  // PICK LIST & PRINT LABELS toolbar actions
  const requireSelection = () => {
    if (!selectedIds.size) { openNotify('warn', 'No Selection', 'Select at least one e-order.'); return false; }
    return true;
  };
  const printPickList = () => {
    if (!requireSelection()) return;
    blockedPopupsRef.current = [];
    const ids = Array.from(selectedIds).map(Number).filter(n => Number.isFinite(n) && n > 0);
    ids.forEach((id) => {
      openPop(`${API}/api/eorders/${id}/picklist.pdf`, 'Pick List');
    });
    // After initiating print, bump status NEW -> PICKING for those ids
    bumpStatusToPickingIfNew(ids);
    if (blockedPopupsRef.current.length) {
      const msg = 'Your browser blocked one or more popups.\nPlease click the links below to open them:\n\n' +
        blockedPopupsRef.current.map(b => `${b.label}: ${b.url}`).join('\n');
      alert(msg);
    }
  };
  const openLabelModal = () => {
    if (!requireSelection()) return;
    const ids = Array.from(selectedIds);
    if (ids.length > 1) {
      alert('Please select only one e-order to print labels.');
      return;
    }
    setLabelOrderId(ids[0]);
    setLabelModalOpen(true);
  };


  // row toggles items pane
  const onRowClick = (order) => {
    if (selectedEOrderId === order.id) {
      setSelectedEOrderId(null);
      setSelectedEOrderMeta({ clientname: '', ordernumber: '' });
    } else {
      setSelectedEOrderId(order.id);
      setSelectedEOrderMeta({ clientname: order.clientname, ordernumber: order.ordernumber });
    }
    setDetail(null);
    setEoBottomPaneMode('items');
  };

  const openDetail = async (order, e) => {
    e.stopPropagation();
    try { const j = await fetchJSON(`${API}/api/eorders/${order.id}`); setDetail(j); setEditing(false); setCreating(false); }
    catch { alert('Failed to load details'); }
  };

  const handleLimitUpdate = () => {
    let v = Number(limitInput);
    if (isNaN(v)) v = limit;
    if (v < 1) v = 1;
    if (v > 10000) v = 10000;
    setLimit(v);
    setLimitInput(String(v));
    try { localStorage.setItem(LS_LIMIT_KEY, v); localStorage.setItem(EO_K_LIMIT, String(v)); } catch {}
    setOrders([]);
    setMeta({ total: 0, limit: v, offset: 0 });
    fetchOrders(0, v);
  };

  // ==== INVENTORY PICKER wiring (available / persist) ====
  const fetchAvailableForPicker = async (filters) => {
    const params = new URLSearchParams();
    if (pickerFor?.clientid) params.set('clientid', pickerFor.clientid);
    ['q','sku','lot','pallet','area','location','receiptid','limit','offset'].forEach(k=>{
      const v = filters?.[k];
      if (v !== undefined && v !== null && String(v) !== '') params.set(k, v);
    });
    const url = `${API}/api/inventory/available${params.toString() ? `?${params.toString()}` : ''}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  };

  const persistPicksForPicker = async (rowsPicked) => {
    if (!pickerFor?.id || !Array.isArray(rowsPicked) || !rowsPicked.length) {
      return { success:false, added:0 };
    }
    const items = rowsPicked.map(r => ({
      inventoryid: Number(r.inventoryid),
      boxes:  Math.max(0, Number(r.boxes  || 0)),
      pieces: Math.max(0, Number(r.pieces || 0)),
    })).filter(x => x.inventoryid && (x.boxes > 0 || x.pieces > 0));
    if (!items.length) return { success:false, added:0 };

    const res = await fetch(`${API}/api/eorders/${pickerFor.id}/items/allocate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: getUserFullName(), items }),
    });
    const j = await res.json().catch(()=>({}));
    if (!res.ok) throw new Error(j?.error || 'Failed to allocate');
    return j; // { success: true, allocated: N }
  };

  // Delete a single E-Order and refresh the list
  const deleteEOrder = async (eorderId) => {
    if (!eorderId) return;
    try {
      const res = await fetch(`${API}/api/eorders/${eorderId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || j?.error) {
        throw new Error(j?.error || 'Delete failed');
      }

      // Refresh the grid
      fetchOrders(meta.offset, limit);
    } catch (err) {
      openNotify('error', 'Delete Failed', err.message || 'Failed to delete e-order');
    }
  };

  // Reusable confirm modal (same look/feel as Orders)
  function ConfirmDialog({ open, title = 'Confirm', message = '', confirmText = 'Yes', cancelText = 'Cancel', onConfirm, onClose }) {
    if (!open) return null;
    return (
      <div className="assign-modal-overlay" onClick={onClose}>
        <div className="assign-modal" onClick={(e) => e.stopPropagation()}>
          <div className="assign-head">
            <h3>{title}</h3>
            <button className="mini-btn" onClick={onClose} aria-label="Close">×</button>
          </div>
          <div className="assign-search">
            <div style={{ color: 'var(--text-color,#eaeef5)' }}>{message}</div>
          </div>
          <div className="assign-actions">
            <button className="btn" onClick={onClose}>{cancelText}</button>
            <button
              className="btn primary"
              onClick={() => {
                try { if (typeof onConfirm === 'function') onConfirm(); }
                finally { if (typeof onClose === 'function') onClose(); }
              }}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    );
  }

  function NoticeModal({ open, type = 'success', title = '', message = '', onClose }) {
    if (!open) return null;
    const color = type === 'error' ? '#ef4444' : type === 'warn' ? '#f59e0b' : '#21bb50';
    return (
      <div className="assign-modal-overlay" onClick={onClose}>
        <div className="assign-modal" onClick={(e) => e.stopPropagation()}>
          <div className="assign-head" style={{ borderBottom: '1px solid var(--border-color, #444)' }}>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 999, background: color }} />
              {title || (type === 'error' ? 'Something went wrong' : 'Notice')}
            </h3>
            <button className="mini-btn" onClick={onClose} aria-label="Close">×</button>
          </div>
          <div className="assign-search" style={{ paddingTop: 6 }}>
            <div style={{ color: 'var(--text-color,#eaeef5)' }}>{message}</div>
          </div>
          <div className="assign-actions">
            <button className="btn primary" onClick={onClose}>OK</button>
          </div>
        </div>
      </div>
    );
  }

  // ===== EDIT MODE =====
  if (editing && selected) {
    return (
      <>
        {newOpen && (
          <NewEOrder
            open={newOpen}
            onClose={() => setNewOpen(false)}
            onCreated={() => { setNewOpen(false); fetchOrders(0, limit); }}
          />
        )}
        {calcOpen && <ShippingCalculator onClose={() => setCalcOpen(false)} />}

        <EOrderEditWorkspace
          eorder={selected}
          editor={
            <>
              {/* ORDER INFO */}
              <fieldset className="neo-card">
                <legend>Order Info</legend>
                <div className="neo-grid">
                  {!IS_CLIENT && (
                    <label className="neo-field">
                      <span>Client</span>
                      <input className="neo-input" value={selected.clientname || ''} disabled />
                    </label>
                  )}
                  <label className="neo-field required">
                    <span>Order Number</span>
                    <input
                      className="neo-input"
                      value={selected.ordernumber || ''}
                      onChange={e => setSelected(s => ({ ...s, ordernumber: e.target.value }))}
                    />
                  </label>
                  <label className="neo-field">
                    <span>Order Date</span>
                    <input
                      type="date"
                      className="neo-input"
                      value={selected.orderdate ? String(selected.orderdate).substring(0, 10) : ''}
                      onChange={e => setSelected(s => ({ ...s, orderdate: e.target.value }))}
                    />
                  </label>
                  <label className="neo-field">
                    <span>Our Status</span>
                    <select
                      className="neo-select"
                      value={selected.our_order_status || ''}
                      onChange={e => setSelected(s => ({ ...s, our_order_status: Number(e.target.value) }))}
                    >
                      <option value="">— Select —</option>
                      {statusOptions.map(opt => (
                        <option key={opt.id} value={opt.id}>{opt.name}</option>
                      ))}
                    </select>
                  </label>
                </div>
              </fieldset>

              {/* SHIP TO */}
              <fieldset className="neo-card">
                <legend>Ship To</legend>
                <div className="neo-grid">
                  <label className="neo-field">
                    <span>Company</span>
                    <input
                      className="neo-input"
                      value={selected.shippingcompany || ''}
                      onChange={e => setSelected(s => ({ ...s, shippingcompany: e.target.value }))}
                    />
                  </label>
                  <label className="neo-field">
                    <span>First Name</span>
                    <input
                      className="neo-input"
                      value={selected.shippingfirstname || ''}
                      onChange={e => setSelected(s => ({ ...s, shippingfirstname: e.target.value }))}
                    />
                  </label>
                  <label className="neo-field">
                    <span>Last Name</span>
                    <input
                      className="neo-input"
                      value={selected.shippinglastname || ''}
                      onChange={e => setSelected(s => ({ ...s, shippinglastname: e.target.value }))}
                    />
                  </label>
                  <label className="neo-field">
                    <span>Email</span>
                    <input
                      className="neo-input"
                      value={selected.shippingemail || ''}
                      onChange={e => setSelected(s => ({ ...s, shippingemail: e.target.value }))}
                    />
                  </label>

                  <label className="neo-field neo-span-2">
                    <span>Address 1</span>
                    <input
                      className="neo-input"
                      value={selected.shippingaddress1 || ''}
                      onChange={e => setSelected(s => ({ ...s, shippingaddress1: e.target.value }))}
                    />
                  </label>
                  <label className="neo-field neo-span-2">
                    <span>Address 2</span>
                    <input
                      className="neo-input"
                      value={selected.shippingaddress2 || ''}
                      onChange={e => setSelected(s => ({ ...s, shippingaddress2: e.target.value }))}
                    />
                  </label>

                  <label className="neo-field">
                    <span>City</span>
                    <input
                      className="neo-input"
                      value={selected.shippingcity || ''}
                      onChange={e => setSelected(s => ({ ...s, shippingcity: e.target.value }))}
                    />
                  </label>

                  <label className="neo-field">
                    <span>Country</span>
                    <select
                      className="neo-select"
                      value={selected.shippingcountry || 'US'}
                      onChange={e => setSelected(s => ({ ...s, shippingcountry: e.target.value }))}
                    >
                      {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                  </label>

                  <label className="neo-field">
                    <span>State/Prov</span>
                    {String(selected.shippingcountry || 'US') === 'US' ? (
                      <select
                        className="neo-select"
                        value={selected.shippingstate || ''}
                        onChange={e => setSelected(s => ({ ...s, shippingstate: e.target.value }))}
                      >
                        <option value="">— State —</option>
                        {US_STATES.map(st => <option key={st.code} value={st.code}>{st.name}</option>)}
                      </select>
                    ) : (
                      <input
                        className="neo-input"
                        value={selected.shippingstate || ''}
                        onChange={e => setSelected(s => ({ ...s, shippingstate: e.target.value }))}
                      />
                    )}
                  </label>

                  <label className="neo-field">
                    <span>Postal Code</span>
                    <input
                      className="neo-input"
                      value={selected.shippingpostalcode || ''}
                      onChange={e => setSelected(s => ({ ...s, shippingpostalcode: e.target.value }))}
                    />
                  </label>

                  <label className="neo-field">
                    <span>Phone</span>
                    <input
                      className="neo-input"
                      value={selected.shippingphone || ''}
                      onChange={e => setSelected(s => ({ ...s, shippingphone: e.target.value }))}
                    />
                  </label>
                </div>
              </fieldset>

              {/* META */}
              <fieldset className="neo-card">
                <legend>Meta</legend>
                <div className="neo-grid" style={{ marginBottom: 12 }}>
                  <label className="neo-field neo-span-2">
                    <span>Tracking #</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        className="neo-input"
                        value={trackingInput}
                        onChange={e => setTrackingInput(e.target.value)}
                        title={(selectedTrackingNums || []).join(', ')}
                        placeholder="Comma-separated if multiple labels"
                      />
                      {/* Tracking numbers now save with the main Save action */}
                    </div>
                  </label>
                  <label className="neo-field">
                    <span>Shipping Method</span>
                    <input
                      className="neo-input"
                      value={selected.shippingmethod || ''}
                      onChange={e => setSelected(s => ({ ...s, shippingmethod: e.target.value }))}
                      placeholder="e.g., UPS Ground"
                    />
                  </label>
                  <label className="eow-neo-field">
                    <span>Shipping Account #</span>
                    <input
                      className="neo-input"
                      value={selected.shippingaccount || ''}
                      onChange={e => setSelected(s => ({ ...s, shippingaccount: e.target.value }))}
                      placeholder="e.g., 1Z..., FedEx, DHL"
                    />
                  </label>
                </div>
                <div className="neo-grid">
                  <label className="neo-field neo-span-2">
                    <span>Notes</span>
                    <textarea
                      className="neo-textarea"
                      rows={3}
                      value={selected.notes || ''}
                      onChange={e => setSelected(s => ({ ...s, notes: e.target.value }))}
                    />
                  </label>
                </div>
              </fieldset>

              <div className="actions" style={{ display:'flex', justifyContent:'flex-end', gap:8, paddingTop:12, paddingRight:12 }}>
                <button
                  className="btn"
                  type="button"
                  onClick={() => {
                    // Close editor and keep the edited e-order visible in list
                    if (selected?.id) {
                      pendingScrollIdRef.current = selected.id;
                      setSelectedEOrderId(selected.id);
                    }
                    setSelected(null);
                    setEditing(false);
                    setDetail(null);
                  }}
                >
                  Cancel
                </button>
                <button className="btn primary" onClick={saveOrder} disabled={saving}>
                  <FaRegSave size={16}/> {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </>
          }
        />
      </>
    );
  }

  // ===== LIST MODE =====
  const pageBlock = (
    <div className="eorders-wrapper">
      <div className="eorders-left-pane">
        
        <div className="toolbar eorders-toolbar">
          {/* First row: Search + Client */}
          <div className="eorders-toolbar-row">
            {/* search */}
            <div className="toolbar search">
              <FaSearch />
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search order #, Internal ID, contact email, notes"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setOrders([]);
                    setMeta({ total: 0, limit, offset: 0 });
                    fetchOrders(0, limit);
                  }
                }}
              />
              {q && (
                <button className="clear" onClick={() => { setQ(''); setOrders([]); setMeta({ total: 0, limit, offset: 0 }); fetchOrders(0, limit); }}>
                  <FaTimes />
                </button>
              )}
            </div>

            {/* Client filter: internal users text search */}
            {!IS_CLIENT && (
              <div className="toolbar search" style={{ minWidth: 360 }}>
                <FaSearch />
                <input
                  value={clientFilter}
                  onChange={(e)=> {
                    const value = e.target.value;
                    setClientFilter(value);
                    const match = clientOptions.find(opt => (opt.name || '').toLowerCase() === value.toLowerCase() || String(opt.id) === value);
                    if (match) {
                      setSelectedClientId(match.id);
                      setClientNameQuery('');
                      setOrders([]); setMeta({ total: 0, limit, offset: 0 });
                      fetchOrders(0, limit, match.id, '');
                    } else {
                      setSelectedClientId('');
                      setClientNameQuery(value.trim());
                    }
                  }}
                  placeholder="Search by client name"
                  list="client-autocomplete"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setOrders([]); setMeta({ total: 0, limit, offset: 0 });
                      fetchOrders(0, limit);
                    }
                  }}
                />
                <datalist id="client-autocomplete">
                  {filteredClientOptions.map(opt => (<option key={opt.id} value={opt.name} />))}
                </datalist>
                {clientFilter && (
                  <button
                    className="clear"
                    onClick={() => {
                      setClientFilter(''); setSelectedClientId(''); setClientNameQuery('');
                      setOrders([]); setMeta({ total: 0, limit, offset: 0 }); fetchOrders(0, limit);
                    }}
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            )}

            {/* Client dropdown: visible for client users to scope by company */}
            {IS_CLIENT && !singleClientOption && (
              <div className="client-select">
                <label htmlFor="eorders-client-select" style={{ marginRight: 6 }}>Client</label>
                <select
                  id="eorders-client-select"
                  className="client-dropdown"
                  value={selectedClientId || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedClientId(val);
                    setClientNameQuery('');
                    setOrders([]); setMeta({ total: 0, limit, offset: 0 });
                    fetchOrders(0, limit, val || '', '');
                  }}
                >
                  <option value="">— All —</option>
                  {clientOptions.map(opt => (
                    <option key={opt.id} value={String(opt.id)}>{opt.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Second row: Preset / From / To / Service / Status / Billed / Deleted */}
          <div className="eorders-toolbar-row">
            {/* Date preset + range */}
            <div className="toolbar search inline-label preset" style={{ minWidth: 180 }}>
              <span className="inline-label-text">Preset</span>
              <select
                className="rd-input rd-select with-inline-label"
                value={datePreset}
                onChange={(e) => applyDatePreset(e.target.value)}
                title="Quick date ranges"
              >
                <option value="">Custom…</option>
                <option value="all">All</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="this_week">This Week</option>
                <option value="last_week">Last Week</option>
                <option value="this_month">This Month</option>
                <option value="last_month">Last Month</option>
                <option value="this_year">This Year</option>
                <option value="last_year">Last Year</option>
              </select>
            </div>

            <div className="toolbar search" style={{ minWidth: 180 }}>
              <span style={{ fontSize: 13, opacity: 0.8 }}>From:</span>
              <input
                type="date"
                className="rd-input"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setDatePreset(''); localStorage.setItem(LS_DATE_PRESET, '');
                  setOrders([]); setMeta({ total: 0, limit, offset: 0 }); fetchOrders(0, limit);
                }}
              />
            </div>

            <div className="toolbar search" style={{ minWidth: 180 }}>
              <span style={{ fontSize: 13, opacity: 0.8 }}>To:</span>
              <input
                type="date"
                className="rd-input"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setDatePreset(''); localStorage.setItem(LS_DATE_PRESET, '');
                  setOrders([]); setMeta({ total: 0, limit, offset: 0 }); fetchOrders(0, limit);
                }}
              />
            </div>

            {/* filters */}
            <div className="toolbar search" style={{ minWidth: 200 }} title="Service Type">
              <span style={{ fontSize: 13, opacity: 0.8 }}>Service:</span>
              <select
                className="rd-input rd-select"
                value={serviceTypeFilter}
                onChange={e => { setServiceTypeFilter(e.target.value); setOrders([]); setMeta({ total: 0, limit, offset: 0 }); }}
              >
                <option value="">All</option>
                {serviceTypes.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="toolbar search compact" style={{ paddingRight: 6 }} title="Our Status">
              <span style={{ fontSize: 13, opacity: 0.8, marginRight: 4 }}>Status:</span>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <MultiSelectStatuses
                  options={ourStatuses}
                  value={ourStatusIds}
                  onChange={(ids) => { setOurStatusIds(ids.map(String)); setOrders([]); setMeta({ total: 0, limit, offset: 0 }); }}
                />
              </div>
            </div>

            {/* Billed filter (internal only) */}
            {!IS_CLIENT && (
              <div className="toolbar search compact" title="Billed">
                <span style={{ fontSize: 13, opacity: 0.8, marginRight: 6 }}>Billed:</span>
                <select
                  className="rd-input rd-select"
                  value={billedFilter}
                  onChange={(e) => { setBilledFilter(e.target.value); setOrders([]); setMeta({ total: 0, limit, offset: 0 }); fetchOrders(0, limit); }}
                >
                  <option value="">All</option>
                  <option value="unbilled">Unbilled only</option>
                  <option value="billed">Billed only</option>
                </select>
              </div>
            )}

            <div className="toolbar" style={{ minWidth: 'auto', gap: 6 }}>
              <span style={{ fontSize: 13, opacity: 0.8 }}>Show deleted</span>
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={e => {
                  setShowDeleted(e.target.checked);
                  setOrders([]); setMeta({ total: 0, limit, offset: 0 }); fetchOrders(0, limit);
                }}
                title="Show deleted"
                style={{ width: 16, height: 16 }}
              />
            </div>
          </div>
        </div>

        {/* ---- button groups ---- */}
        <span className="divider" />
        <div className="toolbar-actions" style={{ width:'100%', display:'flex', gap: 8, flexWrap:'wrap' }}>

          {/* Sync Orders + New */}
          {!IS_CLIENT && (
            <div className="toolbar-group">
              <button className="btn primary" onClick={onNew}>
                <FaPlus /> New
              </button>
              <button className="btn" onClick={syncOrdersNow} disabled={syncing}>
                <FaSync style={{ marginRight: 6 }} />
                {syncing ? 'Syncing…' : 'Sync Orders'}
              </button>
            </div>
          )}
          {IS_CLIENT && (
            <div className="toolbar-group">
              <button className="btn primary" onClick={onNew}>
                <FaPlus /> New
              </button>
            </div>
          )}

          {/* Buy Shipping */}
          {!IS_CLIENT && (
            <div className="toolbar-group">
              <button
                className="btn"
                onClick={openLabelModal}
                title={canBuyShipping ? 'Buy Shipping' : 'Buy Shipping (disabled at/after READY TO SHIP)'}
                disabled={!canBuyShipping}
              >
                <FaDollarSign style={{ marginRight: 6 }} /> Buy Shipping
              </button>
            </div>
          )}

          {/* Pick/Pack/Print buttons */}
          {!IS_CLIENT && (
            <div className="toolbar-group">
              <button className="btn" onClick={printPickListOnly} title="Print Pick List">
                <FaPrint style={{ marginRight: 6 }} /> Pick List
              </button>
              <button className="btn" onClick={printPickAndShip} title="Print Shipping Labels">
                <FaPrint style={{ marginRight: 6 }} /> Labels
              </button>
              <button className="btn" onClick={printPackSlipsOnly} title="Packing Slip">
                <FaPrint style={{ marginRight: 6 }} /> Pack List
              </button>
              <button className="btn" onClick={printAll} title="Pick+Pack (one PDF) + Labels (separate)">
                <FaPrint style={{ marginRight: 6 }} /> Print All
              </button>
            </div>
          )}

          {/* Shipping Calculator */}
          {!IS_CLIENT && (
            <div className="toolbar-group">
              <button className="btn" onClick={() => setCalcOpen(true)} title="Open Shipping Rate Calculator">
                <FaCalculator style={{ marginRight: 6, verticalAlign: '-2px' }} /> Shipping Calculator
              </button>
            </div>
          )}

          {/* More… consolidated actions */}
          <div className="toolbar-group" ref={moreRef}>
            <button className="btn" onClick={() => setMoreOpen(v=>!v)} title="More actions">
              <FaEllipsisH style={{ marginRight: 6 }} /> More…
            </button>
            {moreOpen && (
              <div className="ms-popover fixed more-menu" style={{ position:'fixed', top: moreStyle.top, left: moreStyle.left, width: moreStyle.width }}>
                <div className="ms-list" role="menu" aria-label="More actions">
                  <button
                    className="ms-option"
                    role="menuitem"
                    onClick={openSplitModal}
                    disabled={!canSplitOrder}
                    title={canSplitOrder ? 'Split the selected e-order into a new order' : 'Select exactly one e-order to split'}
                  >
                    <FaClone style={{ marginRight: 8 }} /> Split eOrder
                  </button>
                  <button
                    className="ms-option"
                    role="menuitem"
                    onClick={duplicateEOrder}
                    disabled={!canSplitOrder || duplicateBusy}
                    title={canSplitOrder ? (duplicateBusy ? 'Duplicating…' : 'Duplicate the selected e-order') : 'Select exactly one e-order to duplicate'}
                  >
                    <FaCopy style={{ marginRight: 8 }} /> {duplicateBusy ? 'Duplicating…' : 'Duplicate eOrder'}
                  </button>
                  {!IS_CLIENT && (
                    <button className="ms-option" role="menuitem" onClick={bulkDelete} disabled={!selectedIds.size} title={selectedIds.size ? 'Bulk delete' : 'Select at least one e-order'}>
                      <FaTrash style={{ marginRight: 8 }} /> Bulk Delete
                    </button>
                  )}
                  {!IS_CLIENT && (
                    <button className="ms-option" role="menuitem" onClick={openBulkStatus} disabled={!selectedIds.size} title={selectedIds.size ? 'Bulk status update' : 'Select at least one e-order'}>
                      <FaListUl style={{ marginRight: 8 }} /> Bulk Status
                    </button>
                  )}
                  {!IS_CLIENT && (
                    <button className="ms-option" role="menuitem" onClick={() => { if (!selectedIds.size) { openNotify('warn','No Selection','Select at least one e-order.'); return; } openAssignModal({ id: null }); }} disabled={!selectedIds.size} title={selectedIds.size ? 'Bulk assign' : 'Select at least one e-order'}>
                      <FaUserPlus style={{ marginRight: 8 }} /> Bulk Assign
                    </button>
                  )}
                  {!IS_CLIENT && (
                    <button className="ms-option" role="menuitem" onClick={bulkUnassign} disabled={!selectedIds.size} title={selectedIds.size ? 'Bulk unassign' : 'Select at least one e-order'}>
                      <FaUserMinus style={{ marginRight: 8 }} /> Bulk Unassign
                    </button>
                  )}
                  <button className="ms-option" role="menuitem" onClick={exportCsv}>
                    <FaFileExport style={{ marginRight: 8 }} /> Export CSV
                  </button>
                  <button className="ms-option" role="menuitem" onClick={handleDownloadEOrdersTemplate}>
                    <FaDownload style={{ marginRight: 8 }} /> Download Template
                  </button>
                  <button className="ms-option" role="menuitem" onClick={() => eorderImportInputRef.current?.click()} disabled={eordImportLoading}>
                    {eordImportLoading && <span className="spinner" style={{ marginRight: 6 }} />}
                    <FaUpload style={{ marginRight: 8 }} /> Import CSV
                  </button>
                </div>
              </div>
            )}
            {/* Hidden file input for Import */}
            <input
              ref={eorderImportInputRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files && e.target.files[0];
                if (file) handleEOrdersImportPreview(file);
              }}
            />
          </div>
          </div>
        </div>

        {/* E-Orders Import Preview Modal */}
        {showEordImportPreview && (
          <div className="modal-backdrop" onClick={() => setShowEordImportPreview(false)}>
            <div
              className="modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="eordImportPreviewTitle"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: 1000, width: '95vw', maxHeight: '90vh' }}
            >
              <div className="modal-header">
                <h3 id="eordImportPreviewTitle">E-Orders Import Preview</h3>
                <button
                  className="icon-btn"
                  onClick={() => setShowEordImportPreview(false)}
                  aria-label="Close preview"
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p>
                  Review the data below. Rows with <span className="preview-valid">✓</span> are valid and will be imported.
                  Rows with <span className="preview-error">!</span> have errors and must be corrected before importing.
                </p>
                {eordPreviewData.length > 0 && (
                  <div className="preview-summary">
                    <strong>{eordValidCount}</strong> valid, <strong>{eordInvalidCount}</strong> invalid
                  </div>
                )}
                {eordPreviewErrors.length > 0 && (
                  <div className="help-section">
                    <h4>Errors</h4>
                    <ul className="error-list">
                      {eordPreviewErrors.map((err, idx) => (
                        <li key={idx} className="error-item">{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="preview-table-wrapper" style={{ overflow: 'auto' }}>
                  <table className="preview-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Order #</th>
                        <th>SKU</th>
                        <th>Qty</th>
                        <th>Client Id</th>
                        <th>Client</th>
                        <th>Company</th>
                        <th>First</th>
                        <th>Last</th>
                        <th>Address1</th>
                        <th>Address2</th>
                        <th>City</th>
                        <th>State</th>
                        <th>Postal</th>
                        <th>Country</th>
                        <th>Phone</th>
                        <th>Email</th>
                        <th>Errors</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eordPreviewData.map((row, idx) => (
                        <tr key={idx} className={row.valid ? 'valid-row' : 'error-row'}>
                          <td>{row.valid ? <span className="preview-valid">✓</span> : <span className="preview-error">!</span>}</td>
                          <td>{row.ordernumber}</td>
                          <td>{row.sku}</td>
                          <td>{row.qty}</td>
                          <td>{row.clientid || ''}</td>
                          <td>{row.clientname || ''}</td>
                          <td>{row.shippingcompany || ''}</td>
                          <td>{row.shippingfirstname || ''}</td>
                          <td>{row.shippinglastname || ''}</td>
                          <td>{row.shippingaddress1 || ''}</td>
                          <td>{row.shippingaddress2 || ''}</td>
                          <td>{row.shippingcity || ''}</td>
                          <td>{row.shippingstate || ''}</td>
                          <td>{row.shippingpostalcode || ''}</td>
                          <td>{row.shippingcountry || ''}</td>
                          <td>{row.shippingphone || ''}</td>
                          <td>{row.shippingemail || ''}</td>
                          <td>
                            {(row.errors && row.errors.length > 0) ? (
                              <span className="preview-error" title={row.errors.join('\n')}>!</span>
                            ) : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
      </table>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn secondary" onClick={() => setShowEordImportPreview(false)}>Cancel</button>
                <button
                  className={`btn primary ${eordValidCount === 0 || eordInvalidCount > 0 || eordImportLoading ? 'disabled-grey' : ''}`}
                  onClick={handleEOrdersConfirmImport}
                  disabled={eordValidCount === 0 || eordInvalidCount > 0 || eordImportLoading}
                  title={eordInvalidCount > 0 ? 'Fix all errors in the CSV, then re-upload before importing.' : ''}
                >
                  {eordImportLoading ? 'Importing…' : `Import ${eordValidCount} Valid Row${eordValidCount === 1 ? '' : 's'}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Split eOrder Modal */}
        {splitModalOpen && (
          <div className="modal-backdrop" onClick={closeSplitModal}>
            <div
              className="modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="splitEorderTitle"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: 720, width: '95vw', maxHeight: '90vh' }}
            >
              <div className="modal-header">
                <h3 id="splitEorderTitle">
                  Split e-Order {splitOrderLabel ? `#${splitOrderLabel}` : ''}
                </h3>
                <button className="icon-btn" onClick={closeSplitModal} aria-label="Close split dialog">
                  ×
                </button>
              </div>
              <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p>Select the line items to move into a new order. Lines that already have shipments cannot be moved.</p>
                {splitError && (
                  <div className="error-text" role="alert">
                    {splitError}
                  </div>
                )}
                {splitLinesLoading ? (
                  <div className="loading">Loading line items…</div>
                ) : splitLines.length ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                      <div>
                        <strong>{splitSelectedLines.size}</strong> selected of{' '}
                        <strong>{splitLines.length}</strong> line{splitLines.length === 1 ? '' : 's'}.
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" className="btn" onClick={selectAllSplitLines} disabled={splitBusy}>
                          Select all
                        </button>
                        <button
                          type="button"
                          className="btn"
                          onClick={clearSplitSelection}
                          disabled={splitBusy || splitSelectedLines.size === 0}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                    <div className="eorders-table-scroll-wrapper" style={{ maxHeight: '45vh', overflow: 'auto' }}>
                      <table className="eorders-table" style={{ width: '100%', tableLayout: 'auto' }}>
                        <thead>
                          <tr>
                            <th style={{ width: '1%', textAlign: 'center', padding: '0 6px', whiteSpace: 'nowrap' }}></th>
                            <th>SKU</th>
                            <th>Description</th>
                            <th>Ordered</th>
                            <th>Allocated</th>
                            <th>Shipped</th>
                            <th style={{ width: '12%' }}>Move Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {splitLines.map((line) => {
                            const shipped = Number(line.qtyShipped || 0) > 0;
                            const checked = splitSelectedLines.has(line.id);
                            const available = line.availableQty ?? getLineAvailableQty(line);
                            const disableLine = shipped || available <= 0;
                            const qtyValue = splitQuantities[line.id] ?? 0;
                            return (
                              <tr key={line.id} className={shipped ? 'dimmed' : ''}>
                                <td style={{ width: '1%', textAlign: 'center', padding: '0 6px', whiteSpace: 'nowrap' }}>
                                  <input
                                    type="checkbox"
                                    disabled={disableLine || splitBusy}
                                    checked={checked}
                                    onChange={() => toggleSplitLine(line, disableLine)}
                                    title={
                                      shipped
                                        ? 'This line already has shipments and cannot be moved.'
                                        : available <= 0
                                          ? 'No unallocated quantity is available to move.'
                                          : undefined
                                    }
                                  />
                                </td>
                                <td>{line.sku || '(no sku)'}</td>
                                <td>{line.description || '—'}</td>
                                <td>{line.qtyOrdered ?? 0}</td>
                                <td>{line.qtyAllocated ?? 0}</td>
                                <td>{line.qtyShipped ?? 0}</td>
                                <td>
                                  <input
                                    type="number"
                                    min={1}
                                    max={available || 0}
                                    step="1"
                                    value={qtyValue || ''}
                                    placeholder={available > 0 ? `≤ ${available}` : '0'}
                                    disabled={splitBusy || disableLine}
                                    onChange={(e) => updateSplitQuantity(line, e.target.value)}
                                    style={{ width: 90 }}
                                  />
                                  <div style={{ fontSize: 12, opacity: 0.7 }}>
                                    Max {available}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div style={{ opacity: 0.8 }}>No line items available to move.</div>
                )}
              </div>
              <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button className="btn" onClick={closeSplitModal} disabled={splitBusy}>
                  Cancel
                </button>
                <button
                  className="btn primary"
                  onClick={handleSplitSubmit}
                  disabled={splitBusy || splitLinesLoading || !splitSelectedLines.size}
                >
                  {splitBusy ? 'Splitting…' : 'Create Split'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="eorders-list-tablecol" ref={eordListColRef}>
          {loadingList ? (
            <div className="loading">Loading…</div>
          ) : (
            <>
              <div className="eorders-table-wrap">
                <div className="table-scroll-wrapper">
                  {/* Top progress bar for list refresh */}
                  {listProgress > 0 && (
                    <div
                      style={{
                        position: 'sticky',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 3,
                        background: 'transparent',
                        zIndex: 5,
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          width: `${listProgress}%`,
                          background: 'var(--primary-color)',
                          transition: 'width 180ms ease',
                        }}
                      />
                    </div>
                  )}
                  <table className="eorders-table" ref={eordTableRef}>
                <colgroup>
                  {EORD_COLS.map(k => (
                    <col key={k} style={{ width: eordColWidths[k] ? `${eordColWidths[k]}px` : undefined }} />
                  ))}
                </colgroup>
                <thead>
                  <tr ref={eordHeaderRef}>
                    <th>
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAllOnPage} title="Select all on page" />
                      <span className="col-resizer" onMouseDown={(e)=>startEordResize('select', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEordCol('select'); }} />
                    </th>
                    <th style={{ position: 'relative' }}>
                      Actions
                      <span className="col-resizer" onMouseDown={(e)=>startEordResize('actions', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEordCol('actions'); }} />
                    </th>
                    <th onClick={(e) => handleSort('clientname', e)} className={sortField === 'clientname' ? 'active-sort' : ''} style={{ position: 'relative' }}>
                      Client {sortField === 'clientname' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                      <span className="col-resizer" onMouseDown={(e)=>startEordResize('clientname', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEordCol('clientname'); }} />
                    </th>
                    <th onClick={(e) => handleSort('ordernumber', e)} className={sortField === 'ordernumber' ? 'active-sort' : ''} style={{ position: 'relative' }}>
                      E-Order # {sortField === 'ordernumber' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                      <span className="col-resizer" onMouseDown={(e)=>startEordResize('ordernumber', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEordCol('ordernumber'); }} />
                    </th>
                    <th style={{ position: 'relative' }}>
                      Alt #
                      <span className="col-resizer" onMouseDown={(e)=>startEordResize('altordernumber', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEordCol('altordernumber'); }} />
                    </th>
                    <th onClick={(e) => handleSort('id', e)} className={sortField === 'id' ? 'active-sort' : ''} style={{ position: 'relative', cursor: 'pointer' }}>
                      Internal ID {sortField === 'id' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                      <span className="col-resizer" onMouseDown={(e)=>startEordResize('id', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEordCol('id'); }} />
                    </th>
                    <th onClick={(e) => handleSort('orderdate', e)} className={sortField === 'orderdate' ? 'active-sort' : ''} style={{ position: 'relative' }}>
                      Order Date {sortField === 'orderdate' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                      <span className="col-resizer" onMouseDown={(e)=>startEordResize('orderdate', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEordCol('orderdate'); }} />
                    </th>
                    <th onClick={(e) => handleSort('assignedname', e)} className={sortField === 'assignedname' ? 'active-sort' : ''} style={{ position: 'relative', cursor: 'pointer' }}>
                      Assigned To {sortField === 'assignedname' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                      <span className="col-resizer" onMouseDown={(e)=>startEordResize('assignedname', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEordCol('assignedname'); }} />
                    </th>
                    <th onClick={(e) => handleSort('ourstatusname', e)} className={sortField === 'ourstatusname' ? 'active-sort' : ''} style={{ position: 'relative' }}>
                      Our Status {sortField === 'ourstatusname' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                      <span className="col-resizer" onMouseDown={(e)=>startEordResize('ourstatusname', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEordCol('ourstatusname'); }} />
                    </th>
                    <th onClick={(e) => handleSort('orderstatus', e)} className={sortField === 'orderstatus' ? 'active-sort' : ''} style={{ position: 'relative' }}>
                      Store Status {sortField === 'orderstatus' ? (sortOrder === 'ASC' ? '▲' : '▼') : ''}
                      <span className="col-resizer" onMouseDown={(e)=>startEordResize('orderstatus', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEordCol('orderstatus'); }} />
                    </th>
                    <th style={{ position: 'relative' }}>Ship To
                      <span className="col-resizer" onMouseDown={(e)=>startEordResize('shipto', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEordCol('shipto'); }} />
                    </th>
                    <th style={{ position: 'relative' }}>Ship Address
                      <span className="col-resizer" onMouseDown={(e)=>startEordResize('shipaddress', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEordCol('shipaddress'); }} />
                    </th>
                    <th style={{ position: 'relative' }}>Ship Method
                      <span className="col-resizer" onMouseDown={(e)=>startEordResize('shippingmethod', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEordCol('shippingmethod'); }} />
                    </th>
                    <th style={{ position: 'relative' }}>Tracking #
                      <span className="col-resizer" onMouseDown={(e)=>startEordResize('tracking', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEordCol('tracking'); }} />
                    </th>
                    <th style={{ position: 'relative' }} data-colkey="notes">Notes
                      <span className="col-resizer" onMouseDown={(e)=>startEordResize('notes', e)} onClick={(e)=>{ e.preventDefault(); e.stopPropagation(); }} onDoubleClick={(e)=>{ e.preventDefault(); e.stopPropagation(); autoFitEordCol('notes'); }} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {orders.length ? orders.map(order => {
                    const isDeleted = Number(order.deleted) === 1;
                    const isSelectedRow = selectedEOrderId === order.id;
                    const rowClasses = [isDeleted ? 'deleted-row' : '', isSelectedRow ? 'selected' : (selectedEOrderId ? 'dimmed' : '')].filter(Boolean).join(' ');
                    return (
                      <tr
                        key={order.id}
                        data-eorderid={order.id}
                        className={rowClasses}
                        aria-selected={isSelectedRow || undefined}
                        style={{ cursor: 'pointer', minHeight: '40px', opacity: isDeleted ? 0.65 : undefined }}
                        onClick={() => { setSelectedEOrderId(order.id); setSelectedEOrderMeta({ clientname: order.clientname, ordernumber: order.ordernumber }); setEoBottomPaneMode('items'); }}
                        title={isDeleted ? 'Deleted e-order' : undefined}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(order.id)}
                            onChange={(e) => { e.stopPropagation(); toggleSelect(order.id); }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="td-actions" style={{ whiteSpace: 'nowrap', position: 'relative' }}>
                          <div className="actions">
                            {/* Refresh allocation/status */}
                            {(() => {
                              const statusName = String(order.ourstatusname || '')
                                .toUpperCase()
                                .replace(/\s+/g, ' ')
                                .trim();
                              // Hide refresh action for SKIPPED and DELIVERED orders
                              if (statusName === 'SKIPPED' || statusName === 'DELIVERED') return null;
                              return (
                                <button
                                  className="mini-btn"
                                  title="Refresh allocation/status"
                                  onClick={e => { e.stopPropagation(); refreshEOrder(order); }}
                                  disabled={refreshingIds.has(order.id)}
                                >
                                  <FaSync size={16} style={refreshingIds.has(order.id) ? { animation: 'spin 1s linear infinite' } : undefined} />
                                </button>
                              );
                            })()}
                            {/* Edit — placed right after Refresh */}
                            {(() => {
                              const s = String(order.ourstatusname || '').trim().toUpperCase();
                              if (isDeleted) return null;
                              if (s === 'DELIVERED' && !IS_ADMIN) return null;
                              return (
                                <button
                                  className="mini-btn"
                                  title="Edit"
                                  onClick={e => { e.stopPropagation(); onEdit(order); }}
                                >
                                  <FaEdit size={16} />
                                </button>
                              );
                            })()}
                            {/* View Items removed per request */}

                            {/* Removed: Manual Refresh Carrier Status button */}

                            {/* Add From Inventory (Deducts) */}
                            {!['READY TO SHIP','SHIPPED','DELIVERED','CANCELLED','SKIPPED','ERROR']
                              .includes((order.ourstatusname || '').toUpperCase()) && (
                              <button
                                className="mini-btn"
                                title="Add From Inventory (Deducts)"
                                onClick={(e) => { e.stopPropagation(); openPickerFor(order); }}
                              >
                                <FaBoxOpen size={16} />
                              </button>
                            )}

                            {/* Attachments */}
                            <button
                              className="mini-btn"
                              title="E-Order Attachments"
                              tabIndex={-1}
                              onClick={(e) => { e.stopPropagation(); setSelectedEOrderId(order.id); setSelectedEOrderMeta({ clientname: order.clientname, ordernumber: order.ordernumber }); setEoBottomPaneMode('attachments'); }}
                              style={{ color: Number(order.attachments_count || 0) > 0 ? '#21bb50' : undefined }}
                            >
                              <FaPaperclip size={16} />
                            </button>

                            {/* Assign To User */}
                            <button
                              className="mini-btn"
                              title="Assign To User"
                              onClick={(e) => { e.stopPropagation(); openAssignModal(order); }}
                            >
                              <FaUserPlus size={16} />
                            </button>
                            {order.assignedto ? (
                              <button
                                className="mini-btn"
                                title="Unassign"
                                onClick={async (e) => { e.stopPropagation(); try { const res = await fetch(`${API}/api/eorders/${order.id}/unassign`, { method: 'PUT' }); const j = await res.json(); if (!res.ok || j?.error) throw new Error(j?.error||'Failed'); setOrders(prev => prev.map(o => (o.id===order.id? { ...o, assignedto: null, assignedname: '' } : o))); } catch (err) { openNotify('error','Unassign Failed', err?.message || 'Failed'); } }}
                              >
                                <FaUserMinus size={16} />
                              </button>
                            ) : null}

                            {!isDeleted ? (
                              <>
                                {/* E-Order History (moved here, just before Delete) */}
                                {!IS_CLIENT && (
                                  <button
                                    className="mini-btn"
                                    title="E-Order History"
                                    onClick={e => { e.stopPropagation(); setSelectedEOrderId(order.id); setSelectedEOrderMeta({ clientname: order.clientname, ordernumber: order.ordernumber }); setEoBottomPaneMode('history'); }}
                                  >
                                    <FaHistory size={16} />
                                  </button>
                                )}
                                {(() => {
                                  const s = String(order.ourstatusname || '').trim().toUpperCase();
                                  let blockDelete = (
                                    s === 'SHIPPED' ||
                                    s === 'IN ROUTE' ||
                                    s === 'DELIVERED' ||
                                    s === 'RETURNED TO SENDER'
                                  );
                                  // For client users, only allow Delete when status is exactly NEW
                                  if (IS_CLIENT) blockDelete = (s !== 'NEW') || blockDelete;
                                  if (blockDelete) return null;
                                  return (
                                    <button className="mini-btn danger" title="Delete" onClick={e => { e.stopPropagation(); openConfirm('Delete this e-order?', () => deleteEOrder(order.id)); }}><FaTrash size={16}/></button>
                                  );
                                })()}
                            {/* Invoice actions removed per request */}
                            {/* INTERNAL ONLY: Create/Open Invoice from E-Order charges */}
                            {!IS_CLIENT && (
                              order.invoiceId ? (
                                <button
                                  className="mini-btn"
                                  title="Open invoice"
                                  tabIndex={-1}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (order.invoiceId) window.open(`${API}/api/invoices/${order.invoiceId}/pdf`, '_blank', 'noopener,noreferrer');
                                  }}
                                >
                                  <FaEye size={16} />
                                </button>
                              ) : (
                                <button
                                  className="mini-btn"
                                  title="Create Invoice from E-Order"
                                  tabIndex={-1}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const disp = order.ordernumber || order.id;
                                    openConfirm(`Create a new invoice from E-Order #${disp}?`, () => createInvoiceForEOrder(order.id, order.ordernumber));
                                  }}
                                  style={{ color: '#21bb50' }}
                                >
                                  <FaDollarSign size={16} />
                                </button>
                              )
                            )}
                              </>
                            ) : (
                              <button className="mini-btn" title="Restore" onClick={e => { e.stopPropagation(); restoreOrder(order.id); }} style={{ color: 'limegreen' }}><FaUndo size={16}/></button>
                            )}
                          </div>
                          {rowToasts[order.id] && (
                            <div className="row-toast" role="status" aria-live="polite">
                              {rowToasts[order.id].message}
                            </div>
                          )}
                        </td>
                        <td style={{ minWidth: '150px' }}><BrandIcon type={order.servicetype} /> {order.clientname}</td>
                        <td style={{ minWidth: 'auto', whiteSpace: 'nowrap' }}>
                          {order.ordernumber}
                          {isDeleted && (<span style={{ marginLeft: 8, fontSize: 12, color: '#d64545', fontWeight: 600 }}>DELETED</span>)}
                        </td>
                        <td style={{ minWidth: 'auto', whiteSpace: 'nowrap' }}>{order.altordernumber || ''}</td>
                        <td style={{ minWidth: 'auto', whiteSpace: 'nowrap' }}>{order.id}</td>
                        <td style={{ minWidth: 'auto', whiteSpace: 'nowrap' }}>
                          {(() => {
                            const raw = order.orderdate;
                            if (!raw) return '';
                            const d = new Date(raw);
                            return isNaN(d) ? String(raw) : d.toLocaleString();
                          })()}
                        </td>
                        <td style={{ minWidth: '140px' }}>
                          {(() => {
                            const id = Number(order.assignedto || 0);
                            const name = order.assignedname || '';
                            return (!id || id === 0 || !name) ? 'unassigned' : name;
                          })()}
                        </td>
                        
                        <td
                          style={{
                            minWidth: '150px',
                            color: (() => {
                              const s = (order.ourstatusname || '').toUpperCase();
                              if (s === 'SHIPPED') return 'limegreen';
                              if (s === 'READY TO SHIP') return 'purple';
                              if (s === 'NEW') return 'orange';
                              if (s === 'BACK ORDER') return '#ff5a5a';
                              if (s === 'CANCELLED') return '#d64545'; // <-- red for cancelled
                              return 'inherit';
                            })(),
                            fontWeight: (() => {
                              const s = (order.ourstatusname || '').toUpperCase();
                              return ['SHIPPED','READY TO SHIP','NEW','BACK ORDER','CANCELLED'].includes(s) ? 'bold' : 'normal';
                            })(),
                          }}
                        >
                          {(order.ourstatusname || '').toString().toUpperCase()}
                        </td>

                        <td style={{ minWidth: '150px',
                          color: (() => {
                            const isManual = String(order.servicetype || '').toLowerCase() === 'manual';
                            if (isManual) return 'inherit';
                            const os = (order.orderstatus || '').toLowerCase();
                            return os === 'fulfilled' ? 'limegreen' : os === 'unfulfilled' ? 'orange' : os === 'new' ? 'purple' : 'inherit';
                          })(),
                          fontWeight: (() => {
                            const isManual = String(order.servicetype || '').toLowerCase() === 'manual';
                            if (isManual) return 'normal';
                            const os = (order.orderstatus || '').toLowerCase();
                            return ['fulfilled', 'unfulfilled', 'new'].includes(os) ? 'bold' : 'normal';
                          })(),
                        }}>
                          {(() => {
                            const isManual = String(order.servicetype || '').toLowerCase() === 'manual';
                            return isManual ? '' : (order.orderstatus || '').toString().toUpperCase();
                          })()}
                        </td>
                        <td style={{ minWidth: '160px' }}>
                          {(() => {
                            const fullName = [order.shippingfirstname, order.shippinglastname].filter(Boolean).join(' ');
                            const company = order.shippingcompany || '';
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {fullName && <div>{fullName}</div>}
                                {company && <div>{company}</div>}
                              </div>
                            );
                          })()}
                        </td>
                        <td style={{ minWidth: '220px' }} title={(() => {
                            const a1 = order.shippingaddress1 || '';
                            const a2 = order.shippingaddress2 || '';
                            const city = order.shippingcity || '';
                            const st = order.shippingstate || '';
                            const pc = order.shippingpostalcode || '';
                            const country = order.shippingcountry || '';
                            return [a1, a2, [city, st].filter(Boolean).join(', '), pc, country].filter(Boolean).join(', ');
                          })()}>
                          {(() => {
                            const a1 = order.shippingaddress1 || '';
                            const a2 = order.shippingaddress2 || '';
                            const city = order.shippingcity || '';
                            const st = order.shippingstate || '';
                            const pc = order.shippingpostalcode || '';
                            const country = order.shippingcountry || '';
                            return (
                              <div className="ship-address">
                                {a1 && <div>{a1}</div>}
                                {a2 && <div>{a2}</div>}
                                {(city || st || pc) && (
                                  <div className="line-compact">
                                    {[city, st].filter(Boolean).join(', ')}{(city || st) && pc ? ' ' : ''}{pc}
                                  </div>
                                )}
                                {country && country !== 'US' && <div>{country}</div>}
                              </div>
                            );
                          })()}
                        </td>
                        <td style={{ minWidth: '140px' }} title={order.shippingmethod || ''}>
                          {order.shippingmethod || ''}
                        </td>
                        <td style={{ minWidth: '150px' }}>
                          {(() => {
                            const arr = trackingById[String(order.id)] || [];
                            if (!arr.length) return '';
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {arr.map((t, idx) => {
                                  const href = buildTrackingUrl(order.shippingmethod || '', t.num);
                                  return href ? (
                                    <a
                                      key={idx}
                                      href={href}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ whiteSpace: 'nowrap' }}
                                    >
                                      {t.num}
                                    </a>
                                  ) : (
                                    <span key={idx} style={{ whiteSpace: 'nowrap' }}>
                                      {t.num}
                                    </span>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </td>
                        <td style={{ minWidth: '200px' }} data-colkey="notes">{order.notes}</td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan={EORD_COLS.length} style={{ minHeight: '40px' }}>No orders</td></tr>
                  )}
                </tbody>
              </table>
                </div>

                {/* Pagination controls (bottom of list, like Orders) */}
                <div className="pagination">
                  <button className="btn" disabled={page <= 1} onClick={() => gotoPage(1)}>&laquo;</button>
                  <button className="btn" disabled={page <= 1} onClick={() => gotoPage(page - 1)}>Prev</button>
                  <span>Page {page} of {lastPage}</span>
                  <button className="btn" disabled={page >= lastPage} onClick={() => gotoPage(page + 1)}>Next</button>
                  <button className="btn" disabled={page >= lastPage} onClick={() => gotoPage(lastPage)}>&raquo;</button>
                  <label style={{ marginLeft: 12, fontSize: 13 }}>
                    Rows per page:&nbsp;
                    <input
                      type="number"
                      min={1}
                      max={10000}
                      value={limitInput}
                      style={{ width: 60, fontSize: 13, padding: '2px 4px' }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleLimitUpdate();
                        }
                      }}
                      onChange={e => setLimitInput(e.target.value)}
                    />
                  </label>
                </div>
              </div>

              {assignOpen && (
                <div className="modal-overlay" onClick={closeAssignModal}>
                  <div className="modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-head">
                      <h3 style={{ margin: 0 }}>{assignFor?.id ? `Assign Order #${assignFor?.ordernumber || assignFor.id}` : 'Bulk Assign To User'}</h3>
                      <button className="mini-btn" onClick={closeAssignModal} aria-label="Close">×</button>
                    </div>
                    <div className="modal-content">
                      <div className="form-grid" style={{ gridTemplateColumns: 'minmax(220px, 1fr)' }}>
                        <label>User
                          <select className="neo-input" value={assignUserId} onChange={e => setAssignUserId(e.target.value)}>
                            <option value="">Select a user…</option>
                            {assignUsers.map(u => (
                              <option key={u.userid} value={String(u.userid)}>{u.fullname || u.login || u.email || u.userid}</option>
                            ))}
                          </select>
                        </label>
                      </div>
                    </div>
                    <div className="modal-actions">
                      <button className="btn" onClick={closeAssignModal}>Cancel</button>
                      <button className="btn primary" onClick={async () => {
                        if (!assignUserId) return;
                        try {
                          if (assignFor?.id) { await doAssignTo(); return; }
                          const body = { userid: Number(assignUserId), ids: Array.from(selectedIds) };
                          const res = await fetch(`${API}/api/eorders/bulk/assign`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
                          const j = await res.json();
                          if (!res.ok || j?.error) throw new Error(j?.error || 'Failed');
                          const name = j.assignedname || (assignUsers.find(u => String(u.userid) === String(assignUserId))?.fullname || '');
                          setOrders(prev => prev.map(o => (selectedIds.has(o.id) ? { ...o, assignedto: Number(assignUserId), assignedname: name } : o)));
                          closeAssignModal();
                          showToast({ id: 'eorders.bulk.assign', message: `Assigned ${selectedIds.size} order${selectedIds.size===1?'':'s'}.`, duration: 2000, align: 'center', slideFrom: 'top', pill: true });
                        } catch (e) {
                          openNotify('error', 'Bulk Assign Failed', e?.message || 'Failed to assign');
                        }
                      }} disabled={!assignUserId}>Assign</button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
  );

  // Resizable columns for E-Orders list
  

  return (
    <>
      {newOpen && (<NewEOrder open={newOpen} onClose={() => setNewOpen(false)} onCreated={() => { setNewOpen(false); fetchOrders(0, limit); }} />)}
      {calcOpen && <ShippingCalculator onClose={() => setCalcOpen(false)} />}

      {eoBottomPaneMode && selectedEOrderId ? (
        <div className="eorders-split-root">
          <Split className="eorders-vertical-split" 
            direction="vertical" 
            sizes={eoSplitHeights}
            minSize={200} 
            gutterSize={6} 
            style={{ height: '100%' }}
            onDragEnd={(sizes) => {
              setEoSplitHeights(sizes);
              localStorage.setItem(EORD_SPLIT_KEY, JSON.stringify(sizes));
              localStorage.setItem(EORD_SPLIT_PCT, String(sizes[1]));
            }}
            >
            <div className="pane">{pageBlock}</div>
            <div className="pane eorders-pane-bottom">
              <div className="bottom-pane-toolbar">
                <button
                  className={`btn toggle ${eoBottomPaneMode === 'items' ? 'active' : ''}`}
                  onClick={() => setEoBottomPaneMode('items')}
                  disabled={!selectedEOrderId}
                  aria-pressed={eoBottomPaneMode === 'items'}
                  title="Items"
                >
                  <FaListUl style={{ verticalAlign: '-2px' }} />
                  <span style={{ marginLeft: 6 }}>Items</span>
                </button>
                {!IS_CLIENT && (
                  <button
                    className={`btn toggle ${eoBottomPaneMode === 'history' ? 'active' : ''}`}
                    onClick={() => setEoBottomPaneMode('history')}
                    disabled={!selectedEOrderId}
                    aria-pressed={eoBottomPaneMode === 'history'}
                    title="History"
                  >
                    <FaHistory style={{ verticalAlign: '-2px' }} />
                    <span style={{ marginLeft: 6 }}>History</span>
                  </button>
                )}
                <button
                  className={`btn toggle ${eoBottomPaneMode === 'attachments' ? 'active' : ''}`}
                  onClick={() => setEoBottomPaneMode('attachments')}
                  disabled={!selectedEOrderId}
                  aria-pressed={eoBottomPaneMode === 'attachments'}
                  title="Attachments"
                >
                  <FaPaperclip style={{ verticalAlign: '-2px' }} />
                  <span style={{ marginLeft: 6 }}>Attachments</span>
                </button>
                <div style={{ flex: 1 }} />
                <button
                  className="btn"
                  onClick={() => { setEoBottomPaneMode(null); /* keep selected row */ }}
                  title="Close"
                >
                  <FaTimes style={{ verticalAlign: '-2px' }} />
                  <span style={{ marginLeft: 6 }}>Close</span>
                </button>
              </div>

              {eoBottomPaneMode === 'history' && !IS_CLIENT
                ? renderEOrderHistoryPanel({ eorderId: selectedEOrderId, history: eoHistory, loading: eoHistoryLoading })
                : eoBottomPaneMode === 'attachments'
                  ? renderEOrderAttachmentsPanel({
                      eorderId: selectedEOrderId,
                      attachments: eoAttachments,
                      loading: eoAttachmentsLoading,
                      eoAttachDesc,
                      setEoAttachDesc,
                      eoPendingFiles,
                      setEoPendingFiles,
                      eoFileInputRef,
                      eoDragging,
                      setEoDragging,
                      uploadEOrderFiles,
                      fetchEOrderAttachments,
                      deleteEOrderAttachment,
                    })
                  : (
                    <EOrderItems
                      eorderId={selectedEOrderId}
                      clientName={selectedEOrderMeta.clientname}
                      orderNumber={selectedEOrderMeta.ordernumber}
                      onClose={() => setEoBottomPaneMode(null)}
                    />
                  )}
            </div>
          </Split>
        </div>
      ) : pageBlock}

      {/* Print Labels Modal */}
      {labelModalOpen && labelOrderId && (
        <EOrderLabelModal
          eorderId={labelOrderId}
          clientName={orders.find(o => o.id === labelOrderId)?.clientname || ''}
          orderNumber={orders.find(o => o.id === labelOrderId)?.ordernumber || ''}
          onClose={() => { setLabelModalOpen(false); setLabelOrderId(null); }}
        />
      )}

      <SyncResultsModal
        open={syncOpen}
        onClose={() => setSyncOpen(false)}
        data={syncData}
      />

      {/* Spinner for syncing */}
      <SyncSpinnerModal
        open={syncing}
        message={
          syncingNowName
            ? `Syncing: ${syncingNowName}…`
            : `Syncing shopping carts…`
        }
      />

      {/* Bulk Status Modal */}
      {bulkStatusOpen && (
        <div className="assign-modal-overlay" onClick={() => setBulkStatusOpen(false)}>
          <div className="assign-modal" onClick={(e) => e.stopPropagation()}>
            <div className="assign-head">
              <h3>Bulk Status Update</h3>
              <button className="mini-btn" onClick={() => setBulkStatusOpen(false)} aria-label="Close">×</button>
            </div>
            <div className="assign-search" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ color: 'var(--text-color,#eaeef5)' }}>
                Apply a status to <strong>{selectedIds.size}</strong> selected order{selectedIds.size === 1 ? '' : 's'}.
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span>Status</span>
                <select
                  className="rd-input rd-select"
                  value={bulkStatusValue}
                  onChange={(e) => setBulkStatusValue(e.target.value)}
                >
                  <option value="">Select status…</option>
                  {statusOptions.map(opt => (
                    <option key={opt.id} value={String(opt.id)}>{opt.name}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="assign-actions">
              <button className="btn" onClick={() => setBulkStatusOpen(false)}>Cancel</button>
              <button className="btn primary" onClick={applyBulkStatus} disabled={!bulkStatusValue}>Update</button>
            </div>
          </div>
        </div>
      )}

      {/* Inventory Picker (row-level; deducts) */}
      {pickerOpen && pickerFor && (
        <OrderItemsPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onConfirm={async (rowsPicked) => {
            try {
              const result = await persistPicksForPicker(rowsPicked);
              if (result?.success) {
                openNotify('success', 'Inventory Added', `Allocated ${result.allocated ?? rowsPicked.length} line(s) to E-Order #${pickerFor.ordernumber}.`);
                await fetchOrders(meta.offset, limit);
              } else {
                openNotify('warn', 'Nothing Added', result?.error || 'Nothing to add.');
              }
            } catch (err) {
              openNotify('error', 'Add From Inventory Failed', err.message);
            } finally {
              setPickerOpen(false);
              setPickerFor(null);
            }
          }}
          order={{
            orderid: pickerFor?.id ?? null,
            ordernum: pickerFor?.ordernumber ?? pickerFor?.id ?? null,
            clientname: pickerFor?.clientname ?? '',
            created: '',
            carrier: '',
          }}
          clientid={pickerFor?.clientid ?? ''}
          warehouseid={pickerFor?.warehouseid ?? ''}
          fetchAvailable={fetchAvailableForPicker}
          persistPicks={persistPicksForPicker}
          allowBackorder={true}
          orderTypeLabel="E-Order"
          onBackorderAdd={(orderId) => {
            try {
              const ord = orders.find(o => o.id === orderId);
              setPickerOpen(false);
              setPickerFor(null);
              if (ord) setSelectedEOrderMeta({ clientname: ord.clientname, ordernumber: ord.ordernumber });
              setSelectedEOrderId(orderId);
              setEoBottomPaneMode('items');
              fetchOrders(meta.offset, limit);
            } catch {}
          }}
        />
      )}

      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        onClose={closeConfirm}
        onConfirm={() => {
          const fn = confirmState.onConfirm;
          if (typeof fn === 'function') fn();
        }}
      />

      <NoticeModal
        open={notify.open}
        type={notify.type}
        title={notify.title}
        message={notify.message}
        onClose={closeNotify}
      />

    </>
  );
}

// Render helpers for bottom pane (pure components)
function renderEOrderHistoryPanel({ eorderId, history = [], loading = false }) {
  if (!eorderId) return null;
  return (
    <div className="order-items-panel">
      <h3 style={{ margin: 0, marginBottom: 8, color: 'var(--primary-color,#ff6200)' }}>
        E-Order History (E-Order #{eorderId})
      </h3>
      {loading ? (
        <div className="loading">Loading history…</div>
      ) : (
        <div className="table-scroll-wrapper">
          <table className="orders-table" style={{ width: '100%', background: 'transparent' }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Log</th>
              </tr>
            </thead>
            <tbody>
              {history.length ? history.map(r => (
                <tr key={r.id}>
                  <td>{r.date ? new Date(r.date).toLocaleString() : ''}</td>
                  <td>{r.username || ''}</td>
                  <td style={{ whiteSpace: 'pre-line' }}>{r.log || ''}</td>
                </tr>
              )) : (
                <tr><td colSpan={3} style={{ color: '#aaa' }}>No history entries found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function renderEOrderAttachmentsPanel({
  eorderId,
  attachments = [],
  loading = false,
  eoAttachDesc,
  setEoAttachDesc,
  eoPendingFiles,
  setEoPendingFiles,
  eoFileInputRef,
  eoDragging,
  setEoDragging,
  uploadEOrderFiles,
  fetchEOrderAttachments,
  deleteEOrderAttachment,
}) {
  if (!eorderId) return null;
  return (
    <div className="order-attachments-panel">
      <h3 style={{ margin: 0, marginBottom: 8, color: 'var(--text-color,#ff6200)' }}>
        E-Order Attachments (E-Order #{eorderId})
      </h3>
      <div className="attachments-toolbar">
        <div
          className={`dropzone ${eoDragging ? 'dragging' : ''}`}
          onDragEnter={e => { e.preventDefault(); setEoDragging(true); }}
          onDragOver={e => { e.preventDefault(); setEoDragging(true); }}
          onDragLeave={e => { e.preventDefault(); setEoDragging(false); }}
          onDrop={e => { e.preventDefault(); setEoDragging(false); const files = Array.from(e.dataTransfer?.files || []); if (files.length) setEoPendingFiles(files); }}
          title="Drop files here to upload"
        >
          Drop files here
        </div>
        <div className="attach-actions">
          <input
            type="text"
            className="attach-desc"
            placeholder="Description (optional)"
            value={eoAttachDesc}
            onChange={e => setEoAttachDesc(e.target.value)}
          />
          <input
            type="file"
            multiple
            ref={eoFileInputRef}
            style={{ display: 'none' }}
            onChange={e => {
              const files = e.target.files;
              if (files?.length) setEoPendingFiles(Array.from(files));
              if (eoFileInputRef.current) eoFileInputRef.current.value = '';
            }}
          />
          <button className="btn" onClick={() => eoFileInputRef.current?.click()}>Browse…</button>
          <button className="btn primary" disabled={!eoPendingFiles.length} onClick={() => uploadEOrderFiles(eoPendingFiles)}>
            Upload
          </button>
          <button className="btn" onClick={() => fetchEOrderAttachments(eorderId)} title="Refresh">Refresh</button>
        </div>
      </div>

      {!!eoPendingFiles.length && (
        <div className="pending-files">
          {eoPendingFiles.map((f, i) => (
            <span key={f.name + i} className="file-chip">
              {f.name}
              <button type="button" className="chip-x" title="Remove" aria-label={`Remove ${f.name}`} onClick={() => setEoPendingFiles(prev => prev.filter((_, idx) => idx !== i))}>×</button>
            </span>
          ))}
        </div>
      )}

      <div className="table-scroll-wrapper">
        <table className="orders-table attachments-table" style={{ width: '100%', background: 'transparent' }}>
          <thead>
            <tr>
              <th>Actions</th>
              <th>Filename</th>
              <th>Date</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4}>Loading…</td></tr>
            ) : attachments.length ? (
              attachments.map(a => (
                <tr key={a.id || a.filename}>
                  <td className="actions">
                    {a.id && (
                      <button className="mini-btn danger" title="Delete attachment" onClick={() => deleteEOrderAttachment(a.id)}>🗑</button>
                    )}
                  </td>
                  <td>
                    <a href={`${API}/api/eorders/${eorderId}/attachments/${encodeURIComponent(a.filename)}`} target="_blank" rel="noopener noreferrer">
                      {(a.filename || '').split('/').pop()}
                    </a>
                  </td>
                  <td>{a.date ? new Date(a.date).toLocaleString() : ''}</td>
                  <td>{a.description || ''}</td>
                </tr>
              ))
            ) : (
              <tr><td colSpan={4} style={{ color: '#aaa' }}>No attachments</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
